/**
 * API Service - Mock implementation for development
 */

import idpService from './idp';

// Empty string → relative URLs (/api/…) proxied by nginx.
// For local dev without Docker, set REACT_APP_API_URL in .env.development.
const apiRoot = (process.env.REACT_APP_API_URL ?? window.__ENV__?.API_URL ?? '').replace(/\/$/, '');
const API_BASE = `${apiRoot}/api`;

// ── Auth-aware fetch: detect 401 → auto-redirect to IdP SSO ──
let _redirecting = false;
const _nativeFetch = window.fetch.bind(window);
const fetch = async (url, options) => {
  const response = await _nativeFetch(url, options);
  if (response.status === 401 && !_redirecting) {
    // Only redirect if this is NOT the SSO callback page (avoids infinite loop)
    const hasTokenInUrl = window.location.search.includes('token=') || window.location.search.includes('idp_token=');
    if (!hasTokenInUrl) {
      _redirecting = true;
      console.warn('[AUTH] 401 received – token expired, auto-redirecting to IdP…');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      const returnUrl = encodeURIComponent(window.location.href);
      window.location.href = `${apiRoot}/api/auth/login?return_to=${returnUrl}`;
    } else {
      console.warn('[AUTH] 401 received on SSO callback – will not redirect to avoid loop');
    }
  }
  return response;
};

function maskAgentIdentifier(value) {
  const raw = String(value || '').trim();
  if (!raw) return null;
  if (raw.length <= 8) return '***';
  return `${raw.slice(0, 4)}...${raw.slice(-4)}`;
}

function logAgentApi(event, details = {}) {
  console.info(`[AGENT_API] ${event}`, {
    ts: new Date().toISOString(),
    ...details,
  });
}

// Mock data
const MOCK_LOCATIONS = [
  {
    id: 1,
    name: 'Datacenter Barcelona',
    code: 'BCN-DC01',
    building: 'Building A',
    floor: '1',
    room: 'Server Room 1',
    address: 'Barcelona, Spain',
    description: 'Main datacenter'
  },
  {
    id: 2,
    name: 'Datacenter Madrid',
    code: 'MAD-DC01',
    building: 'Building B',
    floor: '2',
    room: 'Server Room 2',
    address: 'Madrid, Spain',
    description: 'Secondary datacenter'
  }
];

const MOCK_RACKS = [
  {
    id: 1,
    location_id: 1,
    code: 'RACK-001',
    name: 'Server Rack 1',
    height_u: 42,
    width_mm: 600,
    depth_mm: 1000,
    description: 'Main server rack',
    front_view: true,
    rear_view: true,
    elements: [
      { id: 101, rack_id: 1, position: 1, position_u: 1, size: 1, size_u: 1, type: 'server', name: 'Server 1', code: 'SRV-001' },
      { id: 102, rack_id: 1, position: 2, position_u: 2, size: 1, size_u: 1, type: 'server', name: 'Server 2', code: 'SRV-002' },
      { id: 103, rack_id: 1, position: 3, position_u: 3, size: 1, size_u: 1, type: 'switch', name: 'Switch 1', code: 'SW-001' },
      { id: 104, rack_id: 1, position: 4, position_u: 4, size: 1, size_u: 1, type: 'patch_panel', name: 'Patch Panel 1', code: 'PP-001' }
    ]
  },
  {
    id: 2,
    location_id: 1,
    code: 'RACK-002',
    name: 'Network Rack 1',
    height_u: 42,
    width_mm: 600,
    depth_mm: 1000,
    description: 'Network equipment rack',
    front_view: true,
    rear_view: false,
    elements: [
      { id: 201, rack_id: 2, position: 1, position_u: 1, size: 1, size_u: 1, type: 'switch', name: 'Core Switch', code: 'CS-001' },
      { id: 202, rack_id: 2, position: 2, position_u: 2, size: 1, size_u: 1, type: 'router', name: 'Router 1', code: 'RT-001' }
    ]
  }
];

const MOCK_SERVERS = [
  {
    id: 1,
    name: 'srv-app-01',
    hostname: 'srv-app-01.adder.local',
    ip_address: '10.10.10.11',
    os: 'Ubuntu 22.04 LTS',
    environment: 'prod',
    status: 'active',
    location_name: 'Datacenter Barcelona',
    rack_name: 'RACK-001',
    rack_id: 1,
    role: 'App server',
    owner: 'Equipo Apps',
    last_check_at: '2026-01-12T10:00:00Z',
    license: 'Ubuntu Advantage',
    license_status: 'vigente',
    license_expires_at: '2027-01-01',
    cpu: '2 x 8 cores',
    ram_gb: 64,
    storage_tb: 2,
    hypervisor: 'VMware',
    cluster: 'vSphere-Cluster-A',
    tags: ['critical', 'frontend']
  },
  {
    id: 2,
    name: 'srv-db-01',
    hostname: 'srv-db-01.adder.local',
    ip_address: '10.10.10.21',
    os: 'RHEL 9',
    environment: 'prod',
    status: 'maintenance',
    location_name: 'Datacenter Madrid',
    rack_name: 'RACK-002',
    rack_id: 2,
    role: 'Database',
    owner: 'Equipo DB',
    last_check_at: '2026-01-14T08:30:00Z',
    license: 'RHEL Subscription',
    license_status: 'vigente',
    license_expires_at: '2026-12-31',
    cpu: '2 x 12 cores',
    ram_gb: 128,
    storage_tb: 6,
    hypervisor: 'Baremetal',
    cluster: 'DB-Cluster-1',
    tags: ['critical', 'db']
  },
  {
    id: 3,
    name: 'srv-cache-01',
    hostname: 'srv-cache-01.adder.local',
    ip_address: '10.10.10.31',
    os: 'Windows Server 2022',
    environment: 'pre',
    status: 'active',
    location_name: 'Datacenter Barcelona',
    rack_name: 'RACK-001',
    rack_id: 1,
    role: 'Cache',
    owner: 'Equipo Apps',
    last_check_at: '2026-01-15T07:00:00Z',
    license: 'Windows Datacenter SA',
    license_status: 'vigente',
    license_expires_at: '2028-06-30',
    cpu: '1 x 12 cores',
    ram_gb: 32,
    storage_tb: 1,
    hypervisor: 'VMware',
    cluster: 'vSphere-Cluster-A',
    tags: ['cache']
  }
];

class APIService {
  /**
   * Get authorization headers with JWT token
   */
  getAuthHeaders() {
    const token = idpService.getToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  // Locations
  // options.includeInactive: los listados piden también las inactivas (el
  // borrado de una ubicación es lógico). El resto de consumidores no lo pasan
  // y siguen recibiendo solo las activas.
  async getLocations(queryString = '', companyId, options = {}) {
    try {
      const params = new URLSearchParams();
      if (companyId) params.set('company_id', companyId);
      if (queryString) params.append('q', queryString);
      if (options.includeInactive) params.set('include_inactive', 'true');

      const url = `${API_BASE}/locations?${params.toString()}`;
      const response = await fetch(url, {
        headers: this.getAuthHeaders(),
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch locations');
      return await response.json();
    } catch (error) {
      console.warn('Using mock data for locations:', error);
      return { data: MOCK_LOCATIONS };
    }
  }

  // KPIs por ubicación: cuántos CIs y redes cuelgan de cada sede (con su rama).
  async getLocationsSummary(companyId) {
    try {
      const params = new URLSearchParams();
      if (companyId) params.set('company_id', companyId);
      const response = await fetch(`${API_BASE}/locations/summary?${params.toString()}`, {
        headers: this.getAuthHeaders(),
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch locations summary');
      return await response.json();
    } catch (error) {
      console.warn('Resumen de ubicaciones no disponible:', error);
      return { data: {} };
    }
  }

  async getLocation(id, companyId) {
    try {
      const params = new URLSearchParams();
      if (companyId) params.set('company_id', companyId);
      const response = await fetch(`${API_BASE}/locations/${id}?${params.toString()}`, {
        headers: this.getAuthHeaders(),
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch location');
      return await response.json();
    } catch (error) {
      console.warn('Using mock data for location:', error);
      return { data: MOCK_LOCATIONS.find(l => l.id === parseInt(id)) };
    }
  }

  // Subredes IPAM vinculadas a una ubicación. Con `branch` incluye las de toda
  // su rama (plantas, salas, filas…).
  async getLocationSubnets(id, companyId, { branch = false } = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    if (branch) params.set('branch', '1');
    const response = await fetch(`${API_BASE}/locations/${id}/subnets?${params.toString()}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to fetch location subnets');
    return await response.json();
  }

  async createLocation(data, companyId) {
    try {
      const params = new URLSearchParams();
      if (companyId) params.set('company_id', companyId);
      const response = await fetch(`${API_BASE}/locations?${params.toString()}`, {
        method: 'POST',
        headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data)
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || payload.message || 'Failed to create location');
      return payload;
    } catch (error) {
      console.error('Error creating location:', error);
      throw error;
    }
  }

  async updateLocation(id, data, companyId) {
    try {
      const params = new URLSearchParams();
      if (companyId) params.set('company_id', companyId);
      const response = await fetch(`${API_BASE}/locations/${id}?${params.toString()}`, {
        method: 'PUT',
        headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to update location');
      return await response.json();
    } catch (error) {
      console.error('Error updating location:', error);
      throw error;
    }
  }

  /**
   * Qué va a pasar si se borra la ubicación: CIs que se quedan sin ubicación,
   * sub-ubicaciones que suben un nivel y notas/enlaces que se van con ella.
   * Lo pinta el diálogo de borrado ANTES de confirmar.
   */
  async getLocationDeletePreview(id, companyId) {
    try {
      const params = new URLSearchParams();
      if (companyId) params.set('company_id', companyId);
      const response = await fetch(`${API_BASE}/locations/${id}/delete-preview?${params.toString()}`, {
        headers: this.getAuthHeaders(),
        credentials: 'include'
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Failed to load delete preview');
      return payload;
    } catch (error) {
      console.error('Error loading location delete preview:', error);
      throw error;
    }
  }

  /**
   * Borra una ubicación.
   *
   * Sin `options.mode` el borrado es FÍSICO: la ubicación desaparece y sus
   * elementos se quedan sin ubicación. Con `mode: 'deactivate'` se conserva la
   * fila desactivada (`is_active = false`), que es el borrado lógico.
   */
  async deleteLocation(id, companyId, options = {}) {
    try {
      const params = new URLSearchParams();
      if (companyId) params.set('company_id', companyId);
      if (options.mode) params.set('mode', options.mode);
      const response = await fetch(`${API_BASE}/locations/${id}?${params.toString()}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
        credentials: 'include'
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || payload.message || 'Failed to delete location');
      return payload;
    } catch (error) {
      console.error('Error deleting location:', error);
      throw error;
    }
  }

  /** Copia una ubicación (configuración sí, identidad no: código y plano quedan vacíos). */
  async cloneLocation(id, data, companyId) {
    try {
      const params = new URLSearchParams();
      if (companyId) params.set('company_id', companyId);
      const response = await fetch(`${API_BASE}/locations/${id}/clone?${params.toString()}`, {
        method: 'POST',
        headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data || {})
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || payload.message || 'Failed to clone location');
      return payload;
    } catch (error) {
      console.error('Error cloning location:', error);
      throw error;
    }
  }

  // Racks
  async getRacks(locationId, companyId, includeElements = false, includeInactive = false) {
    try {
      const params = new URLSearchParams();
      if (companyId) params.set('company_id', companyId);
      if (locationId) params.set('location_id', locationId);
      if (includeElements) params.set('include_elements', 'true');
      // Los inactivos son borrado lógico: el listado de racks los pide para que
      // el filtro «Inactivos» tenga algo que enseñar y se puedan reactivar.
      if (includeInactive) params.set('include_inactive', 'true');

      const response = await fetch(`${API_BASE}/racks?${params.toString()}`, {
        headers: this.getAuthHeaders(),
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch racks');
      return await response.json();
    } catch (error) {
      console.warn('Using mock data for racks:', error);
      const list = locationId ? MOCK_RACKS.filter(r => r.location_id === parseInt(locationId)) : MOCK_RACKS;
      return { data: list };
    }
  }

  async getRack(rackId, companyId) {
    try {
      const params = new URLSearchParams();
      if (companyId) params.set('company_id', companyId);
      const response = await fetch(`${API_BASE}/racks/${rackId}?${params.toString()}`, {
        headers: this.getAuthHeaders(),
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch rack');
      return await response.json();
    } catch (error) {
      console.warn('Using mock data for rack:', error);
      return { data: MOCK_RACKS.find(r => r.id === parseInt(rackId)) };
    }
  }

  async updateRackElementPosition(rackId, elementId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/racks/${rackId}/elements/${elementId}/position?${params.toString()}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to update position');
    }
    return payload;
  }

  async createRack(locationId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const payload = {
      ...data,
      location_id: locationId || data.location_id
    };
    const response = await fetch(`${API_BASE}/racks?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      // Antes se devolvia un "success" falso y el alta fallaba en silencio
      // (p. ej. un codigo repetido). Mejor decir qué ha pasado.
      throw new Error(result.error || result.message || 'Failed to create rack');
    }
    return result;
  }

  async updateRack(rackId, data, companyId) {
    try {
      const params = new URLSearchParams();
      if (companyId) params.set('company_id', companyId);
      const response = await fetch(`${API_BASE}/racks/${rackId}?${params.toString()}`, {
        method: 'PUT',
        headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to update rack');
      return await response.json();
    } catch (error) {
      console.warn('Using mock response for rack update:', error);
      return { success: true, message: 'Rack updated successfully', data };
    }
  }

  async deleteRack(rackId, companyId) {
    try {
      const params = new URLSearchParams();
      if (companyId) params.set('company_id', companyId);
      const response = await fetch(`${API_BASE}/racks/${rackId}?${params.toString()}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to delete rack');
      return await response.json();
    } catch (error) {
      console.warn('Using mock response for rack deletion:', error);
      return { success: true, message: 'Rack deleted successfully' };
    }
  }

  /**
   * Copia un rack: configuración sí (medidas, fabricante, modelo, notas,
   * ubicación), identidad física no (código y número de serie). El clon nace
   * vacío: ni elementos montados ni paneles ciegos.
   */
  async cloneRack(rackId, data, companyId) {
    try {
      const params = new URLSearchParams();
      if (companyId) params.set('company_id', companyId);
      const response = await fetch(`${API_BASE}/racks/${rackId}/clone?${params.toString()}`, {
        method: 'POST',
        headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data || {})
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || payload.message || 'Failed to clone rack');
      return payload;
    } catch (error) {
      console.error('Error cloning rack:', error);
      throw error;
    }
  }

  /**
   * Pasa un rack de borrador a produccion. Asigna codigo real (consume la
   * nomenclatura) y exige ubicacion. No valida los elementos: eso se hace
   * despues con el checklist.
   */
  async promoteRack(rackId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/racks/${rackId}/promote?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data || {})
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || payload.message || 'No se pudo pasar a producción');
    return payload;
  }

  /**
   * Devuelve un rack en produccion a borrador (diseno). Conserva codigo y
   * numeracion; no toca los elementos montados.
   */
  async revertRackToDraft(rackId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/racks/${rackId}/revert-to-draft?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({})
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || payload.message || 'No se pudo volver a borrador');
    return payload;
  }

  /** Checklist de montaje/validacion del rack (por elemento + resumen). */
  async getRackReadiness(rackId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/racks/${rackId}/readiness?${params.toString()}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'No se pudo leer el checklist');
    return payload;
  }

  /** Cambia el estado de montaje de los elementos del rack (en bloque). */
  async setRackInstallState(rackId, installState, elementIds, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const body = { install_state: installState };
    if (elementIds && elementIds.length) body.element_ids = elementIds;
    const response = await fetch(`${API_BASE}/racks/${rackId}/install-state?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body)
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'No se pudo cambiar el estado');
    return payload;
  }

  async addRackFiller(rackId, companyId, position_u, side, filler_type) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/racks/${rackId}/fillers?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ position_u, side, filler_type }),
    });
    if (!response.ok) throw new Error('Failed to add filler');
    return await response.json();
  }

  async removeRackFiller(rackId, companyId, position_u, side) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/racks/${rackId}/fillers?${params}`, {
      method: 'DELETE',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ position_u, side }),
    });
    if (!response.ok) throw new Error('Failed to remove filler');
    return await response.json();
  }

  // Servers
  async getServers(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, value);
      }
    });

    const response = await fetch(`${API_BASE}/servers?${params.toString()}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to fetch servers');
    }
    return payload;
  }

  async getServer(serverId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);

    const response = await fetch(`${API_BASE}/servers/${serverId}?${params.toString()}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to fetch server');
    }
    return payload;
  }

  async getStorageAdvisories(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/dashboard/storage-advisories?${params.toString()}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to fetch storage advisories');
    }
    return payload;
  }

  async deleteServer(serverId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/servers/${serverId}?${params.toString()}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to delete server');
    }
    return payload;
  }

  async createServer(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/servers?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to create server');
    }
    return payload;
  }

  async updateServer(serverId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/servers/${serverId}?${params.toString()}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to update server');
    }
    return payload;
  }

  // Sincronización automática de hipervisor + VMs de un servidor.
  // `data` = { enabled: bool|null, minutes: number|null }; null = heredar el
  // valor por defecto del despliegue.
  async updateServerHypervisorAutoSync(serverId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/servers/${serverId}/hypervisor-auto-sync?${params.toString()}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data || {})
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to update hypervisor auto-sync');
    }
    return payload;
  }

  async deleteServerManualRole(serverId, roleId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/servers/${serverId}/roles/${roleId}?${params.toString()}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to delete manual server role');
    }
    return payload;
  }

  async previewCiMerge(data, companyId) {
    const payload = {
      ...data,
      ...(companyId ? { company_id: companyId } : {}),
    };
    const response = await fetch(`${API_BASE}/merge/preview`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(result.error || result.message || 'Failed to build merge preview');
    }
    return result;
  }

  async executeCiMerge(data, companyId) {
    const payload = {
      ...data,
      ...(companyId ? { company_id: companyId } : {}),
    };
    const response = await fetch(`${API_BASE}/merge/execute`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(result.error || result.message || 'Failed to execute merge');
    }
    return result;
  }

  // Naming rules and counters
  async getNamingRules(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    if (filters.type) params.set('type', filters.type);
    if (filters.include_global === false) params.set('include_global', 'false');
    const response = await fetch(`${API_BASE}/naming/rules?${params.toString()}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to fetch naming rules');
    }
    return payload;
  }

  async createNamingRule(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/naming/rules?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to create naming rule');
    }
    return payload;
  }

  async updateNamingRule(ruleId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/naming/rules/${ruleId}?${params.toString()}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to update naming rule');
    }
    return payload;
  }

  async deleteNamingRule(ruleId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/naming/rules/${ruleId}?${params.toString()}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to delete naming rule');
    }
    return payload;
  }

  async getNamingCounters(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    if (filters.type) params.set('type', filters.type);
    const response = await fetch(`${API_BASE}/naming/counters?${params.toString()}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to fetch counters');
    }
    return payload;
  }

  async resetNamingCounter(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/naming/counters/reset?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to reset counter');
    }
    return payload;
  }

  async suggestName({ type, rackId, locationId, role, roleIds, companyId, consume }) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    if (type) params.set('type', type);
    if (rackId) params.set('rack_id', rackId);
    if (locationId) params.set('location_id', locationId);
    if (role) params.set('role', role);
    if (roleIds && Array.isArray(roleIds) && roleIds.length) {
      params.set('role_ids', roleIds.join(','));
    }
    // `consume` reserva el número en el contador. Los botones ✨ lo usan para
    // que dos generaciones seguidas nunca devuelvan el mismo código; las
    // previsualizaciones que solo rellenan un campo al abrir no lo usan.
    if (consume) params.set('consume', 'true');
    const response = await fetch(`${API_BASE}/naming/suggest?${params.toString()}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to suggest name');
    }
    return payload;
  }

  /**
   * Rellena las reglas y contadores de nomenclatura que le falten a la
   * empresa. Es idempotente: no pisa patrones ni numeraciones existentes.
   */
  async ensureNamingDefaults(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/naming/defaults?${params.toString()}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to seed naming defaults');
    }
    return payload;
  }

  // Roles
  // ── Visibilidad del menú lateral ─────────────────────────────────────────
  // Preferencia del propio usuario, por empresa. El backend devuelve además lo
  // que oculta la política de la empresa (company_hidden) y la unión de ambas.
  async getMenuPreferences(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/ui-preferences/menu?${params.toString()}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to fetch menu preferences');
    }
    return payload.data || { company_id: null, company_hidden: [], user_hidden: [], hidden: [] };
  }

  async saveMenuPreferences(companyId, hiddenKeys, menuMode) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    // hiddenKeys y menuMode son independientes: se envía solo lo que se cambia,
    // así el personalizador puede guardar el modo sin tocar la visibilidad.
    const body = { company_id: companyId };
    if (hiddenKeys !== undefined && hiddenKeys !== null) body.hidden_keys = hiddenKeys;
    if (menuMode) body.menu_mode = menuMode;
    const response = await fetch(`${API_BASE}/ui-preferences/menu?${params.toString()}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body)
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to save menu preferences');
    }
    return payload.data || { company_id: companyId, company_hidden: [], user_hidden: [], hidden: [] };
  }

  // ── Favoritos del menú lateral ────────────────────────────────────────────
  // Dos ámbitos por usuario: `global` (todas sus empresas) y `company` (solo
  // la activa). El backend devuelve siempre las dos listas.
  async getFavorites(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/ui-preferences/favorites?${params.toString()}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to fetch favorites');
    }
    return payload.data || { company_id: companyId ?? null, global: [], company: [] };
  }

  async saveFavorites(companyId, scope, favorites) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/ui-preferences/favorites?${params.toString()}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ company_id: companyId, scope, favorites })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to save favorites');
    }
    return payload.data || { company_id: companyId ?? null, global: [], company: [] };
  }

  // ── Aspecto (estilo + paleta de color) ────────────────────────────────────
  // Lo que el usuario ha elegido para una empresa, y el aspecto por defecto que
  // esa empresa tiene fijado. Enviar skin/palette a null = seguir el de la empresa.
  async getThemePreferences(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/ui-preferences/theme?${params.toString()}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to fetch theme preferences');
    }
    return payload.data || null;
  }

  async saveThemePreferences(companyId, { skin = null, palette = null } = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/ui-preferences/theme?${params.toString()}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ company_id: companyId, skin, palette })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to save theme preferences');
    }
    return payload.data || null;
  }

  // ── Idioma de la interfaz + onboarding ───────────────────────────────────
  // El idioma es del usuario (no de la empresa) y viaja entre dispositivos.
  async getLanguagePreference() {
    const response = await fetch(`${API_BASE}/ui-preferences/language`, {
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to fetch language preference');
    }
    return payload.data || { language: null, onboarding_done: false };
  }

  async saveLanguagePreference(language, { onboardingDone = null } = {}) {
    const body = { language };
    if (onboardingDone !== null) body.onboarding_done = Boolean(onboardingDone);
    const response = await fetch(`${API_BASE}/ui-preferences/language`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body)
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to save language preference');
    }
    return payload.data || null;
  }

  // ── Módulo de idiomas (superadmin) ────────────────────────────────────────
  async getI18nLanguages() {
    const response = await fetch(`${API_BASE}/i18n/admin/languages`, {
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to fetch languages');
    }
    return payload.data || { languages: [], default: 'es', provider: 'google' };
  }

  async createI18nLanguage({ code, name, nameEs = null, autoTranslate = true }) {
    const response = await fetch(`${API_BASE}/i18n/admin/languages`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        code,
        name,
        name_es: nameEs,
        auto_translate: autoTranslate
      })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to create language');
    }
    return payload.data;
  }

  async updateI18nLanguage(code, patch) {
    const response = await fetch(`${API_BASE}/i18n/admin/languages/${encodeURIComponent(code)}`, {
      method: 'PATCH',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(patch)
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to update language');
    }
    return payload.data;
  }

  async deleteI18nLanguage(code) {
    const response = await fetch(`${API_BASE}/i18n/admin/languages/${encodeURIComponent(code)}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to delete language');
    }
    return true;
  }

  async syncI18nCatalog(language, entries, { replace = true } = {}) {
    const response = await fetch(`${API_BASE}/i18n/admin/catalog`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ language, entries, replace })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to sync catalog');
    }
    return payload.data;
  }

  async translateI18nLanguage(code, { limit = 40 } = {}) {
    const response = await fetch(`${API_BASE}/i18n/admin/languages/${encodeURIComponent(code)}/translate`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ limit })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to auto-translate');
    }
    return payload.data;
  }

  async setI18nTranslations(code, entries) {
    const response = await fetch(`${API_BASE}/i18n/admin/translations/${encodeURIComponent(code)}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ entries })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to save translations');
    }
    return payload.data;
  }

  async getI18nBundle(code) {
    const response = await fetch(`${API_BASE}/i18n/bundle/${encodeURIComponent(code)}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to fetch language bundle');
    }
    return payload.data || { language: code, entries: {} };
  }

  // ── Vista de cada listado (lista / fichas / árbol) ────────────────────────
  // Cada listado declara su vista por defecto en el código; aquí se guarda lo
  // que el usuario haya cambiado, por empresa. `view` a null = volver a la
  // vista por defecto de ese listado.
  async getViewPreferences(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/ui-preferences/views?${params.toString()}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to fetch view preferences');
    }
    return payload.data || null;
  }

  async saveViewPreference(companyId, listKey, view = null) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/ui-preferences/views?${params.toString()}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ company_id: companyId, list: listKey, view })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to save view preference');
    }
    return payload.data || null;
  }

  async savePlatformCompanyTheme(companyId, { skin = null, palette = null } = {}) {    const response = await fetch(`${API_BASE}/platform/companies/${companyId}/theme`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ skin, palette })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to save company theme');
    }
    return payload.data?.theme || { skin, palette };
  }

  // ── Panel de plataforma (solo superadmin global) ──────────────────────────
  async getPlatformSummary() {
    const response = await fetch(`${API_BASE}/platform/summary`, {
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to fetch platform summary');
    }
    return payload.data || {};
  }

  async getPlatformCompaniesMenu() {
    const response = await fetch(`${API_BASE}/platform/companies-menu`, {
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to fetch company menu policies');
    }
    return payload.data?.companies || [];
  }

  async savePlatformCompanyMenu(companyId, hiddenKeys) {
    const response = await fetch(`${API_BASE}/platform/companies/${companyId}/menu`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ hidden_keys: hiddenKeys })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to save company menu policy');
    }
    return payload.data || { company_id: companyId, hidden_keys: [] };
  }

  async getRoles(companyId, { objectType, includeGlobal = true, includeInactive = false } = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    if (objectType) params.set('object_type', objectType);
    if (includeGlobal === false) params.set('include_global', 'false');
    if (includeInactive === true) params.set('include_inactive', 'true');

    const response = await fetch(`${API_BASE}/roles?${params.toString()}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to fetch roles');
    }
    return payload;
  }

  async createRole(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/roles?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to create role');
    }
    return payload;
  }

  async updateRole(roleId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/roles/${roleId}?${params.toString()}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to update role');
    }
    return payload;
  }

  async deleteRole(roleId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/roles/${roleId}?${params.toString()}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to delete role');
    }
    return payload;
  }

  async getRoleAssignments(objectType, objectId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    if (objectType) params.set('object_type', objectType);
    if (objectId) params.set('object_id', objectId);

    const response = await fetch(`${API_BASE}/roles/assignments?${params.toString()}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to fetch role assignments');
    }
    return payload;
  }

  /** Equipos que tienen puesto un rol (al revés que `getRoleAssignments`). */
  async getRoleTargets(roleId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/roles/${roleId}/assignments?${params.toString()}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to fetch role targets');
    }
    return payload;
  }

  async assignRoles(objectType, objectId, roleIds, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/roles/assignments?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ object_type: objectType, object_id: objectId, role_ids: roleIds || [] })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || payload.message || 'Failed to assign roles');
    }
    return payload;
  }

  // Documents
  async getDocuments({ objectType, objectId, companyId, includeInactive = false }) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    if (objectType) params.set('object_type', objectType);
    if (objectId) params.set('object_id', objectId);
    if (includeInactive) params.set('include_inactive', 'true');
    const response = await fetch(`${API_BASE}/documents?${params.toString()}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || payload.message || 'Failed to fetch documents');
    return payload;
  }

  async createDocument(data, companyId) {
    const response = await fetch(`${API_BASE}/documents`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ ...data, company_id: companyId })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || payload.message || 'Failed to create document');
    return payload;
  }

  async updateDocument(docId, data, companyId) {
    const response = await fetch(`${API_BASE}/documents/${docId}?${companyId ? `company_id=${companyId}` : ''}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || payload.message || 'Failed to update document');
    return payload;
  }

  async deleteDocument(docId, companyId) {
    const response = await fetch(`${API_BASE}/documents/${docId}?${companyId ? `company_id=${companyId}` : ''}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || payload.message || 'Failed to delete document');
    return payload;
  }

  async listDocumentVersions(docId, companyId) {
    const response = await fetch(`${API_BASE}/documents/${docId}/versions?${companyId ? `company_id=${companyId}` : ''}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || payload.message || 'Failed to fetch versions');
    return payload;
  }

  async addDocumentVersion(docId, data, companyId) {
    const response = await fetch(`${API_BASE}/documents/${docId}/versions?${companyId ? `company_id=${companyId}` : ''}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || payload.message || 'Failed to add version');
    return payload;
  }

  async listDocumentFiles(docId, companyId) {
    const response = await fetch(`${API_BASE}/documents/${docId}/files?${companyId ? `company_id=${companyId}` : ''}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || payload.message || 'Failed to fetch files');
    return payload;
  }

  async addDocumentFile(docId, data, companyId) {
    const response = await fetch(`${API_BASE}/documents/${docId}/files?${companyId ? `company_id=${companyId}` : ''}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || payload.message || 'Failed to add file');
    return payload;
  }

  // Port Elements (Patch Panels, Switches, generic RackElements)
  async getElements(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.set(k, v);
    });
    const response = await fetch(`${API_BASE}/elements?${params.toString()}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'Failed to fetch elements');
    return payload;
  }

  async getPanelPorts(elementId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/elements/${elementId}/ports?${params.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include'
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to fetch ports');
    return d;
  }

  async createElement(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/elements?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'Failed to create element');
    return payload;
  }

  async updateElement(elementId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/elements/${elementId}?${params.toString()}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'Failed to update element');
    return payload;
  }

  /**
   * Edición masiva de colocación (ubicación y/o rack) del CMDB.
   * `entity` es la clave que entiende el backend: elements, chassis,
   * workstations, printers, ip_phones, peripherals, wifi_aps, firewalls,
   * routers, ups. En `data` solo se mandan las claves que cambian:
   * `location_id` y/o `rack_id` (null = quitar).
   */
  async bulkAssignPlacement(entity, ids, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/bulk/placement?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ entity, ids, ...data })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'Failed to bulk update');
    return payload;
  }

  /**
   * Edición masiva de CAMPOS (tipo Odoo): cambia el mismo valor en varios CIs.
   * `fields` solo lleva las claves que se quieren cambiar; el backend valida
   * cada una contra su whitelist (`BULK_EDIT_FIELDS`) y devuelve los fallos
   * fila a fila en `failed`.
   */
  async bulkUpdateFields(entity, ids, fields, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/bulk/update?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ entity, ids, fields })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'Failed to bulk update');
    return payload;
  }

  /**
   * Edición masiva de la DELEGACIÓN de máquinas virtuales.
   * `delegatedCompanyId` = id de la empresa delegada, o `null` para quitarla.
   */
  async bulkAssignDelegation(entity, ids, delegatedCompanyId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/bulk/delegation?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ entity, ids, delegated_company_id: delegatedCompanyId })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'Failed to bulk update');
    return payload;
  }

  // Reclassify a rack element (server <-> storage). element_type is not part of
  // the agent sync, so the change is stable.
  async changeElementType(elementId, elementType, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/elements/${elementId}/change-type?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ element_type: elementType })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'Failed to change element type');
    return payload;
  }

  async deleteElement(elementId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/elements/${elementId}?${params.toString()}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'Failed to delete element');
    return payload;
  }

  // Ports (Patch Panels & Switches)
  async getPorts(elementId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(`${API_BASE}/elements/${elementId}/ports?${params.toString()}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || payload.message || 'Failed to fetch ports');
    return payload;
  }

  async cloneElement(elementId, newName, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/elements/${elementId}/clone?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ new_name: newName }),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to clone element');
    return p;
  }

  async addExtraPorts(elementId, count, connectionType, labelPrefix, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/elements/${elementId}/ports/add?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ count, connection_type: connectionType, label_prefix: labelPrefix }),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to add ports');
    return p;
  }

  async bulkUpdatePorts(elementId, portIds, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/elements/${elementId}/ports/bulk?${params.toString()}`, {
      method: 'PATCH',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ port_ids: portIds, ...data }),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to bulk update ports');
    return p;
  }

  async getIpamVlans(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/vlans?${params.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch VLANs');
    return p;
  }

  async nukeCompanyData(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/setup/nuke?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ company_id: companyId, confirm: 'BORRAR' }),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to nuke company data');
    return p;
  }

  async seedSyntheticCompanyData(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/setup/seed-synthetic?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ company_id: companyId }),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to seed synthetic data');
    return p;
  }

  async updatePort(elementId, portId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const response = await fetch(
      `${API_BASE}/elements/${elementId}/ports/${portId}?${params.toString()}`,
      {
        method: 'PUT',
        headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data)
      }
    );
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || payload.message || 'Failed to update port');
    return payload;
  }

  // ── Switch Canvas (diseñador de interconexión de switches) ───────────────
  async getSwitchCanvas(companyId, includePlanned = false) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    if (includePlanned) params.set('include_planned', 'true');
    const res = await fetch(`${API_BASE}/elements/switch-canvas?${params.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include'
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch switch canvas');
    return p;
  }

  async saveSwitchCanvasLayout(positions, hidden, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const body = {};
    if (positions !== undefined) body.positions = positions;
    if (hidden !== undefined) body.hidden = hidden;
    const res = await fetch(`${API_BASE}/elements/switch-canvas/layout?${params.toString()}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to save switch canvas layout');
    return p;
  }

  // ── Diseñador de Cableado (grafo de extremos) ────────────────────────────
  async getCablingGraph(params = {}, companyId) {
    const qs = new URLSearchParams();
    if (companyId) qs.set('company_id', companyId);
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      qs.set(key, value);
    });
    const res = await fetch(`${API_BASE}/cabling/graph?${qs.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(payload.error || 'Failed to fetch cabling graph');
    return payload;
  }

  async saveCablingLayout(scopeKey, positions, companyId) {
    const qs = new URLSearchParams();
    if (companyId) qs.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/cabling/layout?${qs.toString()}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ scope_key: scopeKey, positions }),
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(payload.error || 'Failed to save cabling layout');
    return payload;
  }

  /**
   * Mueve UN extremo de un cable ya tendido a otro puerto (sin desmontarlo).
   * `end` es 'a' | 'b' para los latiguillos; el cable de pared siempre mueve
   * su lado de panel.
   */
  async moveCablingLink({ kind, id, end, to }, companyId) {
    const qs = new URLSearchParams();
    if (companyId) qs.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/cabling/link/move?${qs.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ kind, id, end, to }),
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(payload.error || 'Failed to move link');
    return payload;
  }

  async getCablingChain(portType, portId, companyId) {
    const qs = new URLSearchParams({ port_type: portType, port_id: portId });
    if (companyId) qs.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/cabling/chain?${qs.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(payload.error || 'Failed to fetch cabling chain');
    return payload;
  }

  /**
   * Diagrama «desde el punto de vista de X»: una cadena física por cada puerto
   * del elemento. `kind`: panel | switch | ap | server | controller.
   */
  async getElementChain(kind, id, companyId) {
    const qs = new URLSearchParams({ kind, id: String(id) });
    if (companyId) qs.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/cabling/element-chain?${qs.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(payload.error || 'No se pudo cargar el diagrama de conexiones');
    return payload;
  }

  /**
   * Enlaces switch ↔ switch que pasan por panel y/o toma de pared.
   * Los usa el Diseñador de switches para pintarlos como «espejo» de puerto.
   */
  async getCablingMirrors(companyId) {
    const qs = new URLSearchParams();
    if (companyId) qs.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/cabling/mirrors?${qs.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(payload.error || 'Failed to fetch cabling mirrors');
    return payload;
  }

  async createCablingLink(a, b, cable = {}, companyId) {
    const qs = new URLSearchParams();
    if (companyId) qs.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/cabling/link?${qs.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ a, b, ...cable }),
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(payload.error || 'Failed to create link');
    return payload;
  }

  async updateCablingLink(kind, id, cable = {}, companyId) {    const qs = new URLSearchParams();
    if (companyId) qs.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/cabling/link?${qs.toString()}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ kind, id, ...cable }),
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(payload.error || 'Failed to update link');
    return payload;
  }

  async deleteCablingLink(kind, id, companyId) {
    const qs = new URLSearchParams({ kind, id });
    if (companyId) qs.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/cabling/link?${qs.toString()}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(payload.error || 'Failed to delete link');
    return payload;
  }

  // ── Connections (PortConnection) ─────────────────────────────────────────

  async getConnection(portType, portId, companyId) {
    const params = new URLSearchParams({ port_type: portType, port_id: portId });
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/connections?${params}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch connection');
    return p;
  }

  async getAllConnections(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/connections/all?${params}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch all connections');
    return p;
  }

  async getElementConnections(elementId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/connections/element/${elementId}?${params}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch element connections');
    return p;
  }

  async createConnection(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/connections?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create connection');
    return p;
  }

  async updateConnection(connId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/connections/${connId}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update connection');
    return p;
  }

  async deleteConnection(connId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/connections/${connId}?${params}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete connection');
    return p;
  }

  // ── Wall Outlets ────────────────────────────────────────────────────────

  async getWallOutlets(companyId, filters = {}) {
    const params = new URLSearchParams({ company_id: companyId, ...filters });
    const res = await fetch(`${API_BASE}/wall-outlets?${params}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch wall outlets');
    return p;
  }

  async createWallOutlet(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/wall-outlets?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create wall outlet');
    return p;
  }

  /**
   * Alta masiva de tomas. Devuelve `{ created, failed }`.
   *
   * El backend responde 400 cuando NO se ha creado ninguna, pero con la lista
   * de fallos fila a fila; eso no es un error de red, así que se devuelve el
   * cuerpo igual para que la tabla pueda marcar cada fila.
   */
  async bulkCreateWallOutlets(items, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/wall-outlets/bulk?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ items }),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok && !Array.isArray(p.failed)) {
      throw new Error(p.error || 'No se pudieron crear las tomas');
    }
    return p;
  }

  async updateWallOutlet(id, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/wall-outlets/${id}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update wall outlet');
    return p;
  }

  async deleteWallOutlet(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/wall-outlets/${id}?${params}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete wall outlet');
    return p;
  }

  // ── IP Phones ────────────────────────────────────────────────────────────

  async getIPPhones(companyId, filters = {}) {
    const params = new URLSearchParams({ company_id: companyId, ...filters });
    const res = await fetch(`${API_BASE}/ip-phones?${params}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch IP phones');
    return p;
  }

  async getIPPhone(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ip-phones/${id}?${params}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch IP phone');
    return p;
  }

  async createIPPhone(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ip-phones?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create IP phone');
    return p;
  }

  async updateIPPhone(id, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ip-phones/${id}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update IP phone');
    return p;
  }

  async deleteIPPhone(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ip-phones/${id}?${params}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete IP phone');
    return p;
  }

  // ── Printers ─────────────────────────────────────────────────────────────

  async getPrinters(filters = {}) {
    const params = new URLSearchParams(filters);
    const res = await fetch(`${API_BASE}/printers?${params}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch printers');
    return p;
  }
  async getPrinterPorts(printerId, companyId) {
    const qs = new URLSearchParams({ company_id: companyId }).toString();
    const res = await fetch(`${API_BASE}/printers/${printerId}/ports?${qs}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch printer ports');
    return p;
  }

  async createPrinter(data, companyId) {
    const qs = companyId ? `?company_id=${companyId}` : '';
    const res = await fetch(`${API_BASE}/printers${qs}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create printer');
    return p;
  }

  async updatePrinter(id, data, companyId) {
    const qs = companyId ? `?company_id=${companyId}` : '';
    const res = await fetch(`${API_BASE}/printers/${id}${qs}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update printer');
    return p;
  }

  async deletePrinter(id, companyId) {
    const qs = companyId ? `?company_id=${companyId}` : '';
    const res = await fetch(`${API_BASE}/printers/${id}${qs}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete printer');
    return p;
  }

  async getPrinterWiFiConfigs(printerId) {
    const res = await fetch(`${API_BASE}/printers/${printerId}/wifi-configs`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch WiFi configs');
    return p;
  }

  async addPrinterWiFiConfig(printerId, data) {
    const res = await fetch(`${API_BASE}/printers/${printerId}/wifi-configs`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to add WiFi config');
    return p;
  }

  async deletePrinterWiFiConfig(printerId, configId) {
    const res = await fetch(`${API_BASE}/printers/${printerId}/wifi-configs/${configId}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete WiFi config');
    return p;
  }

  // ── Workstations ─────────────────────────────────────────────────────────

  async getWorkstations(filters = {}) {
    const params = new URLSearchParams(filters);
    const res = await fetch(`${API_BASE}/workstations?${params}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch workstations');
    return p;
  }

  /** Agregado del dashboard de Equipos (una sola llamada, sin traer la tabla). */
  async getWorkstationsOverview(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/workstations/overview?${params}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch workstations overview');
    return p.data || null;
  }

  // ── Vulnerabilidades (catálogo NVD + KEV) ────────────────────────────────

  /** Agregado para el bloque de vulnerabilidades del dashboard. */
  async getVulnerabilitiesOverview(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.set(key, value);
    });
    const res = await fetch(`${API_BASE}/vulnerabilities/overview?${params}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch vulnerabilities overview');
    return p;
  }

  async getVulnerabilities(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.set(key, value);
    });
    const res = await fetch(`${API_BASE}/vulnerabilities?${params}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch vulnerabilities');
    return p;
  }

  /** Aceptar o descartar con motivo (el backend lo exige). */
  async updateVulnerability(id, payload, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/vulnerabilities/${id}?${params}`, {
      method: 'PATCH',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update vulnerability');
    return p;
  }

  async recomputeVulnerabilities(payload = {}, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/vulnerabilities/recompute?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to recompute vulnerabilities');
    return p;
  }

  // ── Acciones remotas sobre un CI (Fase 3) ────────────────────────────────

  async getCiCommandsCatalog(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.set(key, value);
    });
    const res = await fetch(`${API_BASE}/ci-commands/catalog?${params}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch command catalog');
    return p;
  }

  async getCiCommands(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.set(key, value);
    });
    const res = await fetch(`${API_BASE}/ci-commands?${params}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch commands');
    return p;
  }

  async runCiCommand(payload, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ci-commands?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to run command');
    return p;
  }

  async cancelCiCommand(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ci-commands/${id}/cancel?${params}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to cancel command');
    return p;
  }

  async getWorkstation(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const qs = params.toString();
    const res = await fetch(`${API_BASE}/workstations/${id}${qs ? `?${qs}` : ''}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch workstation');
    return p;
  }

  async getWorkstationPorts(wsId, companyId) {
    const qs = new URLSearchParams({ company_id: companyId }).toString();
    const res = await fetch(`${API_BASE}/workstations/${wsId}/ports?${qs}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch workstation ports');
    return p;
  }

  async createWorkstation(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const suffix = params.toString() ? `?${params.toString()}` : '';
    const res = await fetch(`${API_BASE}/workstations${suffix}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create workstation');
    return p;
  }

  async updateWorkstation(id, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const suffix = params.toString() ? `?${params.toString()}` : '';
    const res = await fetch(`${API_BASE}/workstations/${id}${suffix}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update workstation');
    return p;
  }

  async deleteWorkstation(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const qs = params.toString();
    const res = await fetch(`${API_BASE}/workstations/${id}${qs ? `?${qs}` : ''}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete workstation');
    return p;
  }

  async getWorkstationStorage(workstationId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/workstations/${workstationId}/storage?${params.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch workstation storage inventory');
    return p;
  }

  async updateWorkstationStorage(workstationId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/workstations/${workstationId}/storage?${params.toString()}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update workstation storage inventory');
    return p;
  }

  async getWorkstationComponents(workstationId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/workstations/${workstationId}/components?${params.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch workstation components');
    return p;
  }

  async getWorkstationUsers(workstationId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/workstations/${workstationId}/users?${params.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch workstation users inventory');
    return p;
  }

  // ── CI Notes ──────────────────────────────────────────────────────────────
  async getCiNotes(ciType, ciId, companyId) {
    const params = new URLSearchParams({ ci_type: ciType, ci_id: ciId });
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ci-notes?${params.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch notes');
    return p;
  }

  async createCiNote(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ci-notes?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create note');
    return p;
  }

  async deleteCiNote(noteId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ci-notes/${noteId}?${params.toString()}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete note');
    return p;
  }

  /** Historial de UN elemento: quién lo creó, qué se cambió y cuándo. */
  async getCiHistory(resourceType, resourceId, companyId, limit = 60) {
    const params = new URLSearchParams({
      resource_type: resourceType,
      resource_id: resourceId,
      limit: String(limit),
    });
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/audit/history?${params.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch element history');
    return p;
  }

  // ── Feedback Hub ─────────────────────────────────────────────────────────
  async getFeedbackItems({ companyId, status, category, q, mine = false } = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    if (status) params.set('status', status);
    if (category) params.set('category', category);
    if (q) params.set('q', q);
    if (mine) params.set('mine', 'true');
    const res = await fetch(`${API_BASE}/feedback?${params.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch feedback items');
    return p;
  }

  async createFeedbackItem(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/feedback?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create feedback item');
    return p;
  }

  async updateFeedbackItem(itemId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/feedback/${itemId}?${params.toString()}`, {
      method: 'PATCH',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update feedback item');
    return p;
  }

  async voteFeedbackItem(itemId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/feedback/${itemId}/vote?${params.toString()}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to vote feedback item');
    return p;
  }

  async getFeedbackItem(itemId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/feedback/${itemId}?${params.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch feedback item');
    return p;
  }

  /** Borra el ticket con sus adjuntos e historial. */
  async deleteFeedbackItem(itemId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/feedback/${itemId}?${params.toString()}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo borrar el ticket');
    return p;
  }

  async getFeedbackAttachments(itemId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/feedback/${itemId}/attachments?${params.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch feedback attachments');
    return p.data || [];
  }

  /**
   * Sube un adjunto (fichero o imagen pegada en el editor) de un ticket.
   * Devuelve el registro creado, con `url` / `inline_url` listos para usar.
   */
  async uploadFeedbackAttachment(file, companyId, { kind, feedbackId } = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const formData = new FormData();
    formData.append('file', file);
    if (kind) formData.append('kind', kind);
    if (feedbackId) formData.append('feedback_id', String(feedbackId));
    const { 'Content-Type': _ct, ...authOnly } = this.getAuthHeaders();
    const res = await fetch(`${API_BASE}/feedback/attachments?${params.toString()}`, {
      method: 'POST', headers: authOnly, credentials: 'include', body: formData,
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo subir el adjunto');
    return p.data;
  }

  async deleteFeedbackAttachment(attachmentId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/feedback/attachments/${attachmentId}?${params.toString()}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo borrar el adjunto');
    return p;
  }

  async createWorkstationComponent(workstationId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/workstations/${workstationId}/components?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create workstation component');
    return p;
  }

  async updateWorkstationComponent(workstationId, compId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/workstations/${workstationId}/components/${compId}?${params.toString()}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update workstation component');
    return p;
  }

  async deleteWorkstationComponent(workstationId, compId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/workstations/${workstationId}/components/${compId}?${params.toString()}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete workstation component');
    return p;
  }

  async createWorkstationComponentPort(workstationId, compId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/workstations/${workstationId}/components/${compId}/ports?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create workstation component port');
    return p;
  }

  async updateWorkstationComponentPort(workstationId, compId, portId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/workstations/${workstationId}/components/${compId}/ports/${portId}?${params.toString()}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update workstation component port');
    return p;
  }

  async deleteWorkstationComponentPort(workstationId, compId, portId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/workstations/${workstationId}/components/${compId}/ports/${portId}?${params.toString()}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete workstation component port');
    return p;
  }

  // ── Server Storage Inventory ──────────────────────────────────────────────

  async getServerChangelog(serverId, companyId, limit = 50, offset = 0) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    params.set('limit', limit);
    params.set('offset', offset);
    const res = await fetch(`${API_BASE}/servers/${serverId}/changelog?${params.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch changelog');
    return p;
  }

  async getServerMetrics(serverId, companyId, hours = 24) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    params.set('hours', hours);
    const res = await fetch(`${API_BASE}/servers/${serverId}/metrics?${params.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch metrics');
    return p;
  }

  async getVirtVMMetrics(vid, companyId, hours = 24) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    params.set('hours', hours);
    const res = await fetch(`${API_BASE}/virt/vms/${vid}/metrics?${params.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch metrics');
    return p;
  }

  async getServerStorage(serverId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/servers/${serverId}/storage?${params.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch storage inventory');
    return p;
  }

  async getServerUsers(serverId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/servers/${serverId}/users?${params.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch server users inventory');
    return p;
  }

  async updateServerStorage(serverId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/servers/${serverId}/storage?${params.toString()}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update storage inventory');
    return p;
  }

  async getDashboardAlertsFeed(companyId, limit = 12) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    if (limit) params.set('limit', limit);
    const res = await fetch(`${API_BASE}/dashboard/alerts-feed?${params.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch dashboard alerts');
    return p;
  }

  async getDashboardDuplicateVms(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/dashboard/duplicate-vms?${params.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({ total_groups: 0, groups: [] }));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch duplicate VMs');
    return p;
  }

  async previewMerge(companyId, sourceType, sourceId, targetType, targetId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/merge/preview?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ source_type: sourceType, source_id: sourceId, target_type: targetType, target_id: targetId }),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to preview merge');
    return p;
  }

  async executeMerge(companyId, sourceType, sourceId, targetType, targetId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/merge/execute?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ source_type: sourceType, source_id: sourceId, target_type: targetType, target_id: targetId }),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to execute merge');
    return p;
  }

  // ── Storage Arrays ────────────────────────────────────────────────────────

  async getStorageArrays(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.set(k, v);
    });
    const res = await fetch(`${API_BASE}/storage-arrays?${params.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch storage arrays');
    return p;
  }

  async getStorageArray(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/storage-arrays/${id}?${params.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch storage array');
    return p;
  }

  async createStorageArray(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/storage-arrays?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create storage array');
    return p;
  }

  async updateStorageArray(id, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/storage-arrays/${id}?${params.toString()}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update storage array');
    return p;
  }

  async deleteStorageArray(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/storage-arrays/${id}?${params.toString()}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete storage array');
    return p;
  }

  async getStorageArrayDiskInventory(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/storage-arrays/${id}/storage?${params.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch storage array disk inventory');
    return p;
  }

  async updateStorageArrayDiskInventory(id, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/storage-arrays/${id}/storage?${params.toString()}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update storage array disk inventory');
    return p;
  }

  // ── Rack Element Components (NICs, HBAs, etc.) ──────────────────────────

  async getComponents(elementId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/elements/${elementId}/components?${params}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch components');
    return p;
  }

  async createComponent(elementId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/elements/${elementId}/components?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create component');
    return p;
  }

  async promoteBmc(elementId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/elements/${elementId}/promote-bmc?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to promote BMC');
    return p;
  }

  async updateComponent(elementId, compId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/elements/${elementId}/components/${compId}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update component');
    return p;
  }

  async deleteComponent(elementId, compId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/elements/${elementId}/components/${compId}?${params}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete component');
    return p;
  }

  // ── Chassis Components (PSUs etc.) ────────────────────────────────────────

  async getChassisComponents(chassisId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/chassis/${chassisId}/components?${params}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch chassis components');
    return p;
  }

  async createChassisComponent(chassisId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/chassis/${chassisId}/components?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create chassis component');
    return p;
  }

  async deleteChassisComponent(chassisId, compId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/chassis/${chassisId}/components/${compId}?${params}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete chassis component');
    return p;
  }

  async createComponentPort(elementId, compId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/elements/${elementId}/components/${compId}/ports?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create component port');
    return p;
  }

  async updateComponentPort(elementId, compId, portId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/elements/${elementId}/components/${compId}/ports/${portId}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update component port');
    return p;
  }

  async deleteComponentPort(elementId, compId, portId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/elements/${elementId}/components/${compId}/ports/${portId}?${params}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete component port');
    return p;
  }

  // ── Chassis ───────────────────────────────────────────────────────────────

  async getChassis(filters = {}) {
    const params = new URLSearchParams(filters);
    const res = await fetch(`${API_BASE}/chassis?${params}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch chassis');
    return p;
  }

  async createChassis(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/chassis?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create chassis');
    return p;
  }

  async updateChassis(id, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/chassis/${id}?${params.toString()}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update chassis');
    return p;
  }

  async deleteChassis(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/chassis/${id}?${params.toString()}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete chassis');
    return p;
  }

  async getChassisNodes(chassisId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/chassis/${chassisId}/nodes?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch chassis nodes');
    return p;
  }

  /**
   * Copia un chasis: configuración sí (tipo, slots, PSUs, notas, ubicación),
   * identidad física no (número de serie y posición en el rack). El clon nace
   * sin nodos ni componentes compartidos.
   */
  async cloneChassis(chassisId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/chassis/${chassisId}/clone?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data || {}),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to clone chassis');
    return p;
  }

  async getAuthorizedCompanies() {
    const res = await fetch(`${API_BASE}/companies/all`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch companies');
    return p.companies || [];
  }

  // ── Descargas con cabecera de autorización ──────────────────────────────

  /**
   * Descarga un fichero de la API con la cabecera `Authorization`.
   *
   * Para qué: `window.open('/api/docs/1/download')` o un `<a href="/api/…">`
   * los resuelve el navegador **sin cabeceras**, así que dependen de la cookie
   * `auth_token`. Cuando esa cookie no está (p. ej. el SSO heredado deja el token
   * solo en el cliente, o caducó), la descarga responde «Token missing». Esta
   * función pide el fichero como cualquier otra llamada y lo guarda con un
   * enlace temporal, así que funciona siempre.
   */
  async downloadAuthedFile(path, fallbackName = 'descarga') {
    const url = /^https?:/i.test(path) ? path : `${apiRoot}${path}`;
    const res = await fetch(url, { headers: this.getAuthHeaders(), credentials: 'include' });
    if (!res.ok) {
      const p = await res.json().catch(() => ({}));
      throw new Error(p.error || 'No se pudo descargar el archivo');
    }
    const blob = await res.blob();

    // Nombre real: el que manda el backend en Content-Disposition si se puede leer.
    let filename = fallbackName;
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    if (match && match[1]) {
      try { filename = decodeURIComponent(match[1].trim()); } catch { filename = match[1].trim(); }
    }

    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    return filename;
  }

  // ── IPAM ──────────────────────────────────────────────────────────────────

  /** Catálogo de tipos de subred (global). Cada tipo trae `subnets_count`. */
  async getIpamSubnetTypes(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/subnet-types?${params.toString()}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudieron cargar los tipos de subred');
    return p.data || [];
  }

  async createIpamSubnetType(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/subnet-types?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo crear el tipo de subred');
    return p.data;
  }

  async updateIpamSubnetType(typeId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/subnet-types/${typeId}?${params.toString()}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo actualizar el tipo de subred');
    return p.data;
  }

  async deleteIpamSubnetType(typeId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/subnet-types/${typeId}?${params.toString()}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo borrar el tipo de subred');
    return p;
  }

  async updateIpamBlock(blockId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/blocks/${blockId}?${params.toString()}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo actualizar el bloque');
    return p.data;
  }

  async getIpamBlocks(companyId, filters = {}) {
    const params = new URLSearchParams(filters);
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/blocks?${params.toString()}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch IPAM blocks');
    return p;
  }

  async getIpamBlockSubnets(blockId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/blocks/${blockId}/subnets?${params.toString()}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch block subnets');
    return p;
  }

  async getIpamBlockFreeSpace(blockId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/blocks/${blockId}/free-space?${params.toString()}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch free space');
    return p;
  }

  async getIpamBlockSuggestSubnet(blockId, prefix, companyId) {
    const params = new URLSearchParams({ prefix });
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/blocks/${blockId}/suggest-subnet?${params.toString()}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch subnet suggestions');
    return p;
  }

  async validateIpamSubnetInBlock(blockId, cidr, companyId) {
    const params = new URLSearchParams({ cidr });
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/blocks/${blockId}/validate-subnet?${params.toString()}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to validate subnet');
    return p;
  }

  async createIpamBlock(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/blocks?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create block');
    return p;
  }

  async getIpamSubnets(companyId, filters = {}) {
    const params = new URLSearchParams(filters);
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/subnets?${params.toString()}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch subnets');
    return p;
  }

  async createIpamSubnet(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/subnets?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create subnet');
    return p;
  }

  async updateIpamSubnet(subnetId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/subnets/${subnetId}?${params.toString()}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update subnet');
    return p;
  }

  async deleteIpamSubnet(subnetId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/subnets/${subnetId}?${params.toString()}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete subnet');
    return p;
  }

  /** Resumen de lo que arrastra borrar una subred (IPs, rangos, elementos a desasociar). */
  async getIpamSubnetDeletePreview(subnetId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/subnets/${subnetId}/delete-preview?${params.toString()}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to preview subnet deletion');
    return p;
  }

  /** Resumen de lo que arrastra borrar un bloque (subredes, IPs, elementos a desasociar). */
  async getIpamBlockDeletePreview(blockId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/blocks/${blockId}/delete-preview?${params.toString()}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to preview block deletion');
    return p;
  }

  /** Borra un bloque en cascada (subredes, rangos, IPs) desasociando los elementos. */
  async deleteIpamBlock(blockId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/blocks/${blockId}?${params.toString()}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete block');
    return p;
  }

  async getIpamIPs(companyId, filters = {}) {
    const params = new URLSearchParams(filters);
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/ips?${params.toString()}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch IPs');
    return p;
  }

  async createIpamIP(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/ips?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create IP');
    return p;
  }

  async updateIpamIP(ipId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/ips/${ipId}?${params.toString()}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update IP');
    return p;
  }

  async deleteIpamIP(ipId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/ips/${ipId}?${params.toString()}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete IP');
    return p;
  }

  async createIpamVlan(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/vlans?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create VLAN');
    return p;
  }

  async updateIpamVlan(id, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/vlans/${id}?${params.toString()}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update VLAN');
    return p;
  }

  async deleteIpamVlan(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/vlans/${id}?${params.toString()}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete VLAN');
    return p;
  }

  async getIpamAssignments(companyId, filters = {}) {
    const params = new URLSearchParams(filters);
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/assignments?${params.toString()}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch assignments');
    return p;
  }

  async createIpamAssignment(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/assignments?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create assignment');
    return p;
  }

  async patchIpamAssignment(assignmentId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/assignments/${assignmentId}?${params.toString()}`, {
      method: 'PATCH',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update assignment');
    return p;
  }

  async getIpamNextSubnet(blockId, prefixlen, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/blocks/${blockId}/next-subnet?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ prefixlen }),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to calculate next subnet');
    return p;
  }

  async getIpamNextIp(subnetId, count = 1, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/subnets/${subnetId}/next-ip?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ count }),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to calculate next IP');
    return p;
  }

  async getIpamSubnetMap(subnetId, companyId, limit = 512) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    if (limit !== undefined && limit !== null) params.set('limit', String(limit));
    const res = await fetch(`${API_BASE}/ipam/subnets/${subnetId}/map?${params.toString()}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch subnet map');
    return p;
  }

  async deleteIpamAssignment(assignmentId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/assignments/${assignmentId}?${params.toString()}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete assignment');
    return p;
  }

  async getIpamSubnetNextIp(subnetId, companyId, count = 1) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/subnets/${subnetId}/next-ip?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ count }),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch next free IP');
    return p;
  }

  // ── IPAM Virtual Groups (CARP / VRRP / HSRP) ─────────────────────────────

  async getIpamVirtualGroups(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/virtual-groups?${params.toString()}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch virtual groups');
    return p;
  }

  async createIpamVirtualGroup(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/virtual-groups?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create virtual group');
    return p;
  }

  async updateIpamVirtualGroup(groupId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/virtual-groups/${groupId}?${params.toString()}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update virtual group');
    return p;
  }

  async deleteIpamVirtualGroup(groupId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/virtual-groups/${groupId}?${params.toString()}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete virtual group');
    return p;
  }

  async addIpamVirtualGroupMember(groupId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/virtual-groups/${groupId}/members?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to add member');
    return p;
  }

  async updateIpamVirtualGroupMember(groupId, memberId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/virtual-groups/${groupId}/members/${memberId}?${params.toString()}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update member');
    return p;
  }

  async removeIpamVirtualGroupMember(groupId, memberId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/virtual-groups/${groupId}/members/${memberId}?${params.toString()}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to remove member');
    return p;
  }

  // ── IPAM Ranges ──────────────────────────────────────────────────────────

  async getIpamRanges(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') params.set(k, v); });
    const res = await fetch(`${API_BASE}/ipam/ranges?${params.toString()}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch IPAM ranges');
    return p;
  }

  async createIpamRange(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/ranges?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create range');
    return p;
  }

  async updateIpamRange(rangeId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/ranges/${rangeId}?${params.toString()}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update range');
    return p;
  }

  async deleteIpamRange(rangeId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/ranges/${rangeId}?${params.toString()}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete range');
    return p;
  }

  // ── DHCP Reservations ─────────────────────────────────────────────────────

  async getDhcpStats(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/dhcp/stats?${params.toString()}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch DHCP stats');
    return p;
  }

  async getDhcpReservations(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') params.set(k, v); });
    const res = await fetch(`${API_BASE}/ipam/dhcp/reservations?${params.toString()}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch DHCP reservations');
    return p;
  }

  async createDhcpReservation(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/dhcp/reservations?${params.toString()}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create DHCP reservation');
    return p;
  }

  async updateDhcpReservation(resId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/dhcp/reservations/${resId}?${params.toString()}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update DHCP reservation');
    return p;
  }

  async deleteDhcpReservation(resId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ipam/dhcp/reservations/${resId}?${params.toString()}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete DHCP reservation');
    return p;
  }


  async getAuditLog(filters = {}) {
    try {
      const params = new URLSearchParams(filters);
      const response = await fetch(`${API_BASE}/audit?${params}`);
      if (!response.ok) throw new Error('Failed to fetch audit log');
      return await response.json();
    } catch (error) {
      console.warn('Using empty audit log:', error);
      return { data: [] };
    }
  }

  // ── PDUs ──────────────────────────────────────────────────────────────────

  async getPdus(companyId, rackId = null) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    if (rackId)    params.set('rack_id', rackId);
    const res = await fetch(`${API_BASE}/pdus?${params}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch PDUs');
    return p;
  }

  async getPdu(pduId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/pdus/${pduId}?${params}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch PDU');
    return p;
  }

  async updatePduOutlet(pduId, outletId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/pdus/${pduId}/outlets/${outletId}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update outlet');
    return p;
  }

  /** Update PDU strip-level metadata: pdu_phase, pdu_amperage, parent_pdu_id, upstream_outlet_id */
  async updatePdu(pduId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/pdus/${pduId}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update PDU');
    return p;
  }

  /** Fetch the outlet list for a PDU (used by upstream-outlet selectors) */
  async getPduOutlets(pduId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/pdus/${pduId}/outlets?${params}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch PDU outlets');
    return p;
  }

  // ── Power Connections ─────────────────────────────────────────────────────

  async getPowerConnections(consumerType, consumerId, companyId) {
    const params = new URLSearchParams({ consumer_type: consumerType, consumer_id: consumerId });
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/power-connections?${params}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch power connections');
    return p;
  }

  async createPowerConnection(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/power-connections?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create power connection');
    return p;
  }

  async deletePowerConnection(connId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/power-connections/${connId}?${params}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete power connection');
    return p;
  }

  async updatePowerConnection(connId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/power-connections/${connId}?${params}`, {
      method: 'PATCH',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update power connection');
    return p;
  }

  async getRackPowerSummary(rackId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/racks/${rackId}/power-summary?${params}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch power summary');
    return p;
  }

  // ── Virtualization ──────────────────────────────────────────────────────────

  _virtQ(companyId) {
    const p = new URLSearchParams();
    if (companyId) p.set('company_id', companyId);
    return p.toString();
  }

  async getHypervisors(companyId) {
    const res = await fetch(`${API_BASE}/virt/hypervisors?${this._virtQ(companyId)}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async createHypervisor(data, companyId) {
    const res = await fetch(`${API_BASE}/virt/hypervisors?${this._virtQ(companyId)}`, { method: 'POST', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async updateHypervisor(id, data, companyId) {
    const res = await fetch(`${API_BASE}/virt/hypervisors/${id}?${this._virtQ(companyId)}`, { method: 'PUT', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async deleteHypervisor(id, companyId) {
    const res = await fetch(`${API_BASE}/virt/hypervisors/${id}?${this._virtQ(companyId)}`, { method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }

  async getClusters(companyId) {
    const res = await fetch(`${API_BASE}/virt/clusters?${this._virtQ(companyId)}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async createCluster(data, companyId) {
    const res = await fetch(`${API_BASE}/virt/clusters?${this._virtQ(companyId)}`, { method: 'POST', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async updateCluster(id, data, companyId) {
    const res = await fetch(`${API_BASE}/virt/clusters/${id}?${this._virtQ(companyId)}`, { method: 'PUT', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async deleteCluster(id, companyId) {
    const res = await fetch(`${API_BASE}/virt/clusters/${id}?${this._virtQ(companyId)}`, { method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async addClusterMember(clusterId, memberData, companyId) {
    const res = await fetch(`${API_BASE}/virt/clusters/${clusterId}/members?${this._virtQ(companyId)}`, { method: 'POST', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(memberData), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async removeClusterMember(clusterId, hypervisorId, companyId) {
    const res = await fetch(`${API_BASE}/virt/clusters/${clusterId}/members/${hypervisorId}?${this._virtQ(companyId)}`, { method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }

  async getDatastores(companyId) {
    const res = await fetch(`${API_BASE}/virt/datastores?${this._virtQ(companyId)}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async createDatastore(data, companyId) {
    const res = await fetch(`${API_BASE}/virt/datastores?${this._virtQ(companyId)}`, { method: 'POST', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async updateDatastore(id, data, companyId) {
    const res = await fetch(`${API_BASE}/virt/datastores/${id}?${this._virtQ(companyId)}`, { method: 'PUT', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async deleteDatastore(id, companyId) {
    const res = await fetch(`${API_BASE}/virt/datastores/${id}?${this._virtQ(companyId)}`, { method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }

  async getVSwitches(companyId) {
    const res = await fetch(`${API_BASE}/virt/vswitches?${this._virtQ(companyId)}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async getPortGroups(companyId) {
    const res = await fetch(`${API_BASE}/virt/portgroups?${this._virtQ(companyId)}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async createVSwitch(data, companyId) {
    const res = await fetch(`${API_BASE}/virt/vswitches?${this._virtQ(companyId)}`, { method: 'POST', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async updateVSwitch(id, data, companyId) {
    const res = await fetch(`${API_BASE}/virt/vswitches/${id}?${this._virtQ(companyId)}`, { method: 'PUT', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async deleteVSwitch(id, companyId) {
    const res = await fetch(`${API_BASE}/virt/vswitches/${id}?${this._virtQ(companyId)}`, { method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async createPortGroup(vswitchId, data, companyId) {
    const res = await fetch(`${API_BASE}/virt/vswitches/${vswitchId}/portgroups?${this._virtQ(companyId)}`, { method: 'POST', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async updatePortGroup(vswitchId, pgId, data, companyId) {
    const res = await fetch(`${API_BASE}/virt/vswitches/${vswitchId}/portgroups/${pgId}?${this._virtQ(companyId)}`, { method: 'PUT', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async deletePortGroup(vswitchId, pgId, companyId) {
    const res = await fetch(`${API_BASE}/virt/vswitches/${vswitchId}/portgroups/${pgId}?${this._virtQ(companyId)}`, { method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }

  /** NICs de las VMs (con su port group), para «qué VMs usan estas redes». */
  async getVirtNics(companyId, params = {}) {
    const qs = new URLSearchParams(this._virtQ(companyId));
    if (params.port_group_id) qs.set('port_group_id', params.port_group_id);
    if (params.vswitch_id) qs.set('vswitch_id', params.vswitch_id);
    const res = await fetch(`${API_BASE}/virt/nics?${qs.toString()}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }

  async getVMs(companyId, filters = {}) {
    const params = new URLSearchParams(this._virtQ(companyId));
    if (filters.agent_status) params.set('agent_status', filters.agent_status);
    const res = await fetch(`${API_BASE}/virt/vms?${params.toString()}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async getVM(id, companyId) {
    const res = await fetch(`${API_BASE}/virt/vms/${id}?${this._virtQ(companyId)}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async createVM(data, companyId) {
    const res = await fetch(`${API_BASE}/virt/vms?${this._virtQ(companyId)}`, { method: 'POST', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async updateVM(id, data, companyId) {
    const res = await fetch(`${API_BASE}/virt/vms/${id}?${this._virtQ(companyId)}`, { method: 'PUT', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async deleteVM(id, companyId) {
    const res = await fetch(`${API_BASE}/virt/vms/${id}?${this._virtQ(companyId)}`, { method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async addVMDisk(vmId, data, companyId) {
    const res = await fetch(`${API_BASE}/virt/vms/${vmId}/disks?${this._virtQ(companyId)}`, { method: 'POST', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async deleteVMDisk(vmId, diskId, companyId) {
    const res = await fetch(`${API_BASE}/virt/vms/${vmId}/disks/${diskId}?${this._virtQ(companyId)}`, { method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async addVMNic(vmId, data, companyId) {
    const res = await fetch(`${API_BASE}/virt/vms/${vmId}/nics?${this._virtQ(companyId)}`, { method: 'POST', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async deleteVMNic(vmId, nicId, companyId) {
    const res = await fetch(`${API_BASE}/virt/vms/${vmId}/nics/${nicId}?${this._virtQ(companyId)}`, { method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }

  // ─── Firewalls ─────────────────────────────────────────────────────────────
  _fwQ(companyId) { return `company_id=${companyId}`; }

  async getFirewalls(companyId, params = {}) {
    const qs = new URLSearchParams({ company_id: companyId, ...params }).toString();
    const res = await fetch(`${API_BASE}/firewalls?${qs}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async getFirewall(id, companyId) {
    const res = await fetch(`${API_BASE}/firewalls/${id}?${this._fwQ(companyId)}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async createFirewall(data, companyId) {
    const res = await fetch(`${API_BASE}/firewalls?${this._fwQ(companyId)}`, { method: 'POST', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async updateFirewall(id, data, companyId) {
    const res = await fetch(`${API_BASE}/firewalls/${id}?${this._fwQ(companyId)}`, { method: 'PUT', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async deleteFirewall(id, companyId) {
    const res = await fetch(`${API_BASE}/firewalls/${id}?${this._fwQ(companyId)}`, { method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async cloneFirewall(id, newName, companyId) {
    const res = await fetch(`${API_BASE}/firewalls/${id}/clone?${this._fwQ(companyId)}`, { method: 'POST', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ new_name: newName }), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async addFirewallInterface(fwId, data, companyId) {
    const res = await fetch(`${API_BASE}/firewalls/${fwId}/interfaces?${this._fwQ(companyId)}`, { method: 'POST', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async updateFirewallInterface(fwId, iid, data, companyId) {
    const res = await fetch(`${API_BASE}/firewalls/${fwId}/interfaces/${iid}?${this._fwQ(companyId)}`, { method: 'PUT', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async deleteFirewallInterface(fwId, iid, companyId) {
    const res = await fetch(`${API_BASE}/firewalls/${fwId}/interfaces/${iid}?${this._fwQ(companyId)}`, { method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async getFirewallInterfaces(fwId, companyId) {
    const res = await fetch(`${API_BASE}/firewalls/${fwId}/interfaces?${this._fwQ(companyId)}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async getFirewallPorts(fwId, companyId) {
    const res = await fetch(`${API_BASE}/firewalls/${fwId}/ports?${this._fwQ(companyId)}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }

  // ─── Routers ───────────────────────────────────────────────────────────────
  _rtrQ(companyId) { return `company_id=${companyId}`; }

  async getRouters(companyId, params = {}) {
    const qs = new URLSearchParams({ company_id: companyId, ...params }).toString();
    const res = await fetch(`${API_BASE}/routers?${qs}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async getRouter(id, companyId) {
    const res = await fetch(`${API_BASE}/routers/${id}?${this._rtrQ(companyId)}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async createRouter(data, companyId) {
    const res = await fetch(`${API_BASE}/routers?${this._rtrQ(companyId)}`, { method: 'POST', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async updateRouter(id, data, companyId) {
    const res = await fetch(`${API_BASE}/routers/${id}?${this._rtrQ(companyId)}`, { method: 'PUT', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async deleteRouter(id, companyId) {
    const res = await fetch(`${API_BASE}/routers/${id}?${this._rtrQ(companyId)}`, { method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async addRouterInterface(rtrId, data, companyId) {
    const res = await fetch(`${API_BASE}/routers/${rtrId}/interfaces?${this._rtrQ(companyId)}`, { method: 'POST', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async updateRouterInterface(rtrId, iid, data, companyId) {
    const res = await fetch(`${API_BASE}/routers/${rtrId}/interfaces/${iid}?${this._rtrQ(companyId)}`, { method: 'PUT', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async deleteRouterInterface(rtrId, iid, companyId) {
    const res = await fetch(`${API_BASE}/routers/${rtrId}/interfaces/${iid}?${this._rtrQ(companyId)}`, { method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async getRouterInterfaces(rtrId, companyId) {
    const res = await fetch(`${API_BASE}/routers/${rtrId}/interfaces?${this._rtrQ(companyId)}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async getRouterPorts(routerId, companyId) {
    const res = await fetch(`${API_BASE}/routers/${routerId}/ports?${this._rtrQ(companyId)}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async getRouterMeta(companyId) {
    const res = await fetch(`${API_BASE}/routers/meta?${this._rtrQ(companyId)}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }

  // ─── SAI / UPS ─────────────────────────────────────────────────────────────
  _upsQ(companyId) { return `company_id=${companyId}`; }

  async getUpsList(companyId, params = {}) {
    const qs = new URLSearchParams({ company_id: companyId, ...params }).toString();
    const res = await fetch(`${API_BASE}/ups?${qs}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async getUps(id, companyId) {
    const res = await fetch(`${API_BASE}/ups/${id}?${this._upsQ(companyId)}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async createUps(data, companyId) {
    const res = await fetch(`${API_BASE}/ups?${this._upsQ(companyId)}`, { method: 'POST', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async updateUps(id, data, companyId) {
    const res = await fetch(`${API_BASE}/ups/${id}?${this._upsQ(companyId)}`, { method: 'PUT', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async deleteUps(id, companyId) {
    const res = await fetch(`${API_BASE}/ups/${id}?${this._upsQ(companyId)}`, { method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async getUpsMeta(companyId) {
    const res = await fetch(`${API_BASE}/ups/meta?${this._upsQ(companyId)}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }

  // Tomas de salida del SAI y conexiones de alimentación
  async getUpsOutlets(upsId, companyId) {
    const res = await fetch(`${API_BASE}/ups/${upsId}/outlets?${this._upsQ(companyId)}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to fetch UPS outlets');
    return d;
  }
  async updateUpsOutlet(upsId, outletId, data, companyId) {
    const res = await fetch(`${API_BASE}/ups/${upsId}/outlets/${outletId}?${this._upsQ(companyId)}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to update UPS outlet');
    return d;
  }
  async connectUpsOutlet(upsId, outletId, data, companyId) {
    const res = await fetch(`${API_BASE}/ups/${upsId}/outlets/${outletId}/connect?${this._upsQ(companyId)}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to connect UPS outlet');
    return d;
  }
  async disconnectUpsOutlet(upsId, outletId, companyId) {
    const res = await fetch(`${API_BASE}/ups/${upsId}/outlets/${outletId}/connection?${this._upsQ(companyId)}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to disconnect UPS outlet');
    return d;
  }
  async getUpsConsumers(upsId, companyId) {
    const res = await fetch(`${API_BASE}/ups/${upsId}/consumers?${this._upsQ(companyId)}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to fetch UPS consumers');
    return d;
  }

  // ── WiFi ──────────────────────────────────────────────────────────────────
  _wifiQ(companyId, extra = {}) {
    const p = new URLSearchParams(extra);
    if (companyId) p.set('company_id', companyId);
    return p.toString();
  }

  // Controllers
  async getWifiControllers(companyId, params = {}) {
    const qs = this._wifiQ(companyId, params);
    const res = await fetch(`${API_BASE}/wifi/controllers?${qs}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async getWifiController(id, companyId) {
    const res = await fetch(`${API_BASE}/wifi/controllers/${id}?${this._wifiQ(companyId)}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async createWifiController(data, companyId) {
    const res = await fetch(`${API_BASE}/wifi/controllers?${this._wifiQ(companyId)}`, { method: 'POST', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async updateWifiController(id, data, companyId) {
    const res = await fetch(`${API_BASE}/wifi/controllers/${id}?${this._wifiQ(companyId)}`, { method: 'PUT', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async deleteWifiController(id, companyId) {
    const res = await fetch(`${API_BASE}/wifi/controllers/${id}?${this._wifiQ(companyId)}`, { method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }

  // Access Points
  async getWifiAPs(companyId, params = {}) {
    const qs = this._wifiQ(companyId, params);
    const res = await fetch(`${API_BASE}/wifi/aps?${qs}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async getWifiAP(id, companyId) {
    const res = await fetch(`${API_BASE}/wifi/aps/${id}?${this._wifiQ(companyId)}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async createWifiAP(data, companyId) {
    const res = await fetch(`${API_BASE}/wifi/aps?${this._wifiQ(companyId)}`, { method: 'POST', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async updateWifiAP(id, data, companyId) {
    const res = await fetch(`${API_BASE}/wifi/aps/${id}?${this._wifiQ(companyId)}`, { method: 'PUT', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async deleteWifiAP(id, companyId) {
    const res = await fetch(`${API_BASE}/wifi/aps/${id}?${this._wifiQ(companyId)}`, { method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async cloneWifiAP(id, newName, companyId) {
    const res = await fetch(`${API_BASE}/wifi/aps/${id}/clone?${this._wifiQ(companyId)}`, { method: 'POST', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ new_name: newName }), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  // Puertos del AP (cableado): el uplink es el extremo que se conecta a una
  // toma de pared, a un patch panel o directamente a un switch.
  async getWifiAPPorts(id, companyId) {    const res = await fetch(`${API_BASE}/wifi/aps/${id}/ports?${this._wifiQ(companyId)}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async createWifiAPPort(id, data, companyId) {
    const res = await fetch(`${API_BASE}/wifi/aps/${id}/ports?${this._wifiQ(companyId)}`, { method: 'POST', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async deleteWifiAPPort(id, portId, companyId) {
    const res = await fetch(`${API_BASE}/wifi/aps/${id}/ports/${portId}?${this._wifiQ(companyId)}`, { method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }

  // ── Cadena de almacenamiento: LUN de la cabina ↔ volumen del servidor ──
  // El LUN lo publica una cabina (element_type=storage) y el volumen es lo que
  // ve el servidor/VM (D:, /mnt/datos…). El diseñador dibuja la cadena con eso.
  async getLuns(companyId, elementId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    if (elementId) params.set('element_id', elementId);
    const res = await fetch(`${API_BASE}/luns?${params}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async createLun(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/luns?${params}`, { method: 'POST', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async updateLun(id, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/luns/${id}?${params}`, { method: 'PUT', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async deleteLun(id, companyId, force = false) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    if (force) params.set('force', '1');
    const res = await fetch(`${API_BASE}/luns/${id}?${params}`, { method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) { const e = new Error(d.error || 'Failed'); e.payload = d; throw e; }
    return d;
  }
  async getVolumes(companyId, ciType, ciId, extra = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    if (ciType) params.set('ci_type', ciType);
    if (ciId) params.set('ci_id', ciId);
    // unlinked=1 → sólo volúmenes sin LUN; lun_id=<n> → los de ese LUN
    if (extra.unlinked) params.set('unlinked', '1');
    if (extra.lunId) params.set('lun_id', extra.lunId);
    const res = await fetch(`${API_BASE}/volumes?${params}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async createVolume(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/volumes?${params}`, { method: 'POST', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async updateVolume(id, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/volumes/${id}?${params}`, { method: 'PUT', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async deleteVolume(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/volumes/${id}?${params}`, { method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }

  // SSIDs
  async getWifiSSIDs(companyId, params = {}) {
    const qs = this._wifiQ(companyId, params);
    const res = await fetch(`${API_BASE}/wifi/ssids?${qs}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async createWifiSSID(data, companyId) {
    const res = await fetch(`${API_BASE}/wifi/ssids?${this._wifiQ(companyId)}`, { method: 'POST', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async updateWifiSSID(id, data, companyId) {
    const res = await fetch(`${API_BASE}/wifi/ssids/${id}?${this._wifiQ(companyId)}`, { method: 'PUT', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async deleteWifiSSID(id, companyId) {
    const res = await fetch(`${API_BASE}/wifi/ssids/${id}?${this._wifiQ(companyId)}`, { method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }

  // ── RADIUS servers ──────────────────────────────────────────────────────
  async getWifiRadiusServers(companyId) {
    const res = await fetch(`${API_BASE}/wifi/radius-servers?${this._wifiQ(companyId)}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async createWifiRadiusServer(data, companyId) {
    const res = await fetch(`${API_BASE}/wifi/radius-servers?${this._wifiQ(companyId)}`, { method: 'POST', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(data) });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async updateWifiRadiusServer(id, data, companyId) {
    const res = await fetch(`${API_BASE}/wifi/radius-servers/${id}?${this._wifiQ(companyId)}`, { method: 'PUT', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(data) });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }
  async deleteWifiRadiusServer(id, companyId) {
    const res = await fetch(`${API_BASE}/wifi/radius-servers/${id}?${this._wifiQ(companyId)}`, { method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }

  // ── ISP Operators (Phase 1) ───────────────────────────────────────────────

  _ispQ(companyId) {
    const p = new URLSearchParams();
    if (companyId) p.set('company_id', companyId);
    return p.toString();
  }

  async getIspMeta(companyId) {
    const res = await fetch(`${API_BASE}/isp/meta?${this._ispQ(companyId)}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }

  // Operators
  async getIspOperators(companyId, filters = {}) {
    const p = new URLSearchParams({ ...filters });
    if (companyId) p.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/isp/operators?${p}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to fetch operators');
    return d;
  }

  async getIspOperator(id, companyId) {
    const res = await fetch(`${API_BASE}/isp/operators/${id}?${this._ispQ(companyId)}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }

  async createIspOperator(data, companyId) {
    const res = await fetch(`${API_BASE}/isp/operators?${this._ispQ(companyId)}`, {
      method: 'POST', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include', body: JSON.stringify(data),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to create operator');
    return d;
  }

  async updateIspOperator(id, data, companyId) {
    const res = await fetch(`${API_BASE}/isp/operators/${id}?${this._ispQ(companyId)}`, {
      method: 'PUT', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include', body: JSON.stringify(data),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to update operator');
    return d;
  }

  async deleteIspOperator(id, companyId) {
    const res = await fetch(`${API_BASE}/isp/operators/${id}?${this._ispQ(companyId)}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to delete operator');
    return d;
  }

  // Circuits
  async getIspCircuits(companyId, filters = {}) {
    const p = new URLSearchParams({ ...filters });
    if (companyId) p.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/isp/circuits?${p}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to fetch circuits');
    return d;
  }

  async getIspCircuit(id, companyId) {
    const res = await fetch(`${API_BASE}/isp/circuits/${id}?${this._ispQ(companyId)}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }

  async createIspCircuit(data, companyId) {
    const res = await fetch(`${API_BASE}/isp/circuits?${this._ispQ(companyId)}`, {
      method: 'POST', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include', body: JSON.stringify(data),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to create circuit');
    return d;
  }

  async updateIspCircuit(id, data, companyId) {
    const res = await fetch(`${API_BASE}/isp/circuits/${id}?${this._ispQ(companyId)}`, {
      method: 'PUT', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include', body: JSON.stringify(data),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to update circuit');
    return d;
  }

  async deleteIspCircuit(id, companyId) {
    const res = await fetch(`${API_BASE}/isp/circuits/${id}?${this._ispQ(companyId)}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to delete circuit');
    return d;
  }

  // Demarc points
  async getIspDemarcPoints(circuitId, companyId) {
    const res = await fetch(`${API_BASE}/isp/circuits/${circuitId}/demarc?${this._ispQ(companyId)}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed');
    return d;
  }

  async createIspDemarcPoint(circuitId, data, companyId) {
    const res = await fetch(`${API_BASE}/isp/circuits/${circuitId}/demarc?${this._ispQ(companyId)}`, {
      method: 'POST', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include', body: JSON.stringify(data),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to create demarc point');
    return d;
  }

  async updateIspDemarcPoint(demarc_id, data, companyId) {
    const res = await fetch(`${API_BASE}/isp/demarc/${demarc_id}?${this._ispQ(companyId)}`, {
      method: 'PUT', headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include', body: JSON.stringify(data),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to update demarc point');
    return d;
  }

  async deleteIspDemarcPoint(demarc_id, companyId) {
    const res = await fetch(`${API_BASE}/isp/demarc/${demarc_id}?${this._ispQ(companyId)}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to delete demarc point');
    return d;
  }

  // ── Device catalog: manufacturers & models ──

  async getManufacturers(companyId, { deviceType, q, scope, includeInactive = false, withUsage = false } = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    if (deviceType) params.set('device_type', deviceType);
    if (q) params.set('q', q);
    if (scope) params.set('scope', scope);
    if (includeInactive) params.set('include_inactive', 'true');
    if (withUsage) params.set('with_usage', '1');
    const res = await fetch(`${API_BASE}/catalog/manufacturers?${params}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch manufacturers');
    return p;
  }

  async createManufacturer(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/catalog/manufacturers?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create manufacturer');
    return p;
  }

  async deleteManufacturer(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/catalog/manufacturers/${id}?${params}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete manufacturer');
    return p;
  }

  async getDeviceModels(companyId, { deviceType, manufacturerId, q, scope, includeInactive = false, withUsage = false } = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    if (deviceType) params.set('device_type', deviceType);
    if (manufacturerId) params.set('manufacturer_id', manufacturerId);
    if (q) params.set('q', q);
    if (scope) params.set('scope', scope);
    if (includeInactive) params.set('include_inactive', 'true');
    if (withUsage) params.set('with_usage', '1');
    const res = await fetch(`${API_BASE}/catalog/models?${params}`, { headers: this.getAuthHeaders(), credentials: 'include' });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch models');
    return p;
  }

  async createDeviceModel(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/catalog/models?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create model');
    return p;
  }

  async deleteDeviceModel(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/catalog/models/${id}?${params}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete model');
    return p;
  }

  async updateManufacturer(id, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/catalog/manufacturers/${id}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update manufacturer');
    return p;
  }

  async updateDeviceModel(id, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/catalog/models/${id}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update model');
    return p;
  }

  async cloneDeviceModel(id, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/catalog/models/${id}/clone?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data || {}),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to clone model');
    return p;
  }

  /** Cambia el ámbito: global | company | private. */
  async setCatalogScope(kind, id, scope, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/catalog/${kind}/${id}/scope?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ scope }),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to change scope');
    return p;
  }

  async getDeviceModelUsage(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/catalog/models/${id}/usage?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch usage');
    return p;
  }

  async getCatalogHealth(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/catalog/health?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch catalog health');
    return p;
  }

  async getCatalogDeviceTypes(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/catalog/device-types?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch device types');
    return p;
  }

  // ── Topology / Datacenter Diagram ─────────────────────────────────────────

  async getDatacenterTopology(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/topology/datacenter?${params}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to load topology');
    return p;
  }

  // ── Services ──────────────────────────────────────────────────────────────

  async getServicesMeta(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/services/meta?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    return res.json().catch(() => ({}));
  }

  async getServicesStats(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/services/stats?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    return res.json().catch(() => ({}));
  }

  async getServices(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    const res = await fetch(`${API_BASE}/services?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => []);
    if (!res.ok) throw new Error(p.error || 'Failed to load services');
    return p;
  }

  async getService(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/services/${id}?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to load service');
    return p;
  }

  async createService(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/services?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create service');
    return p;
  }

  async updateService(id, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/services/${id}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update service');
    return p;
  }

  async deleteService(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/services/${id}?${params}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete service');
    return p;
  }

  async getServicesGraph(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/services/graph?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    return res.json().catch(() => ({ nodes: [], edges: [] }));
  }

  // ── Service Access Points ─────────────────────────────────────────────────

  async getServiceAccessPointsMeta(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/services/access-points/meta?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    return res.json().catch(() => ({}));
  }

  async getServiceAccessPoints(svcId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/services/${svcId}/access-points?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({ access_points: [], total: 0 }));
    if (!res.ok) throw new Error(p.error || 'Failed to load access points');
    return p;
  }

  async createServiceAccessPoint(svcId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/services/${svcId}/access-points?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create access point');
    return p;
  }

  async updateServiceAccessPoint(svcId, apId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/services/${svcId}/access-points/${apId}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update access point');
    return p;
  }

  async deleteServiceAccessPoint(svcId, apId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/services/${svcId}/access-points/${apId}?${params}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete access point');
    return p;
  }

  // ── CI Access Points (cómo se llega a un dispositivo, sin credenciales) ───

  async getCiAccessPointsMeta(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ci-access-points/meta?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to load access-point metadata');
    return p;
  }

  async getCiAccessPoints(objectType, objectId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    if (objectType) params.set('object_type', objectType);
    if (objectId) params.set('object_id', objectId);
    const res = await fetch(`${API_BASE}/ci-access-points?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({ access_points: [], total: 0 }));
    if (!res.ok) throw new Error(p.error || 'Failed to load access points');
    return p;
  }

  // Listado global (auditoría): todos los accesos de la empresa con el nombre
  // del equipo al que apuntan. Sirve para encontrar lo creado desde las fichas.
  async getCiAccessPointsAll(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    params.set('with_object', 'true');
    const res = await fetch(`${API_BASE}/ci-access-points?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({ access_points: [], total: 0 }));
    if (!res.ok) throw new Error(p.error || 'Failed to load access points');
    return p;
  }

  async createCiAccessPoint(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ci-access-points?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create access point');
    return p;
  }

  async updateCiAccessPoint(accessId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ci-access-points/${accessId}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update access point');
    return p;
  }

  async deleteCiAccessPoint(accessId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ci-access-points/${accessId}?${params}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete access point');
    return p;
  }

  // ── Estado de credenciales en L6 Passwords ────────────────────────────────
  //
  // Pregunta al backend (que a su vez habla con Passwords servidor a servidor)
  // cuántas credenciales hay vinculadas a cada punto de acceso. Devuelve SOLO
  // recuentos: el secreto nunca sale del vault.
  //
  // Si Passwords no está configurado o no responde, devuelve `{}` en vez de
  // lanzar: la ficha del equipo tiene que seguir funcionando sin la insignia.
  async getCredentialStatus(resources, companyId) {
    const lista = (resources || []).filter(Boolean);
    if (!lista.length) return {};
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    params.set('resources', lista.join(','));
    try {
      const res = await fetch(`${API_BASE}/passwords/credential-status?${params}`, {
        headers: this.getAuthHeaders(), credentials: 'include',
      });
      if (!res.ok) return {};
      const p = await res.json().catch(() => ({}));
      return p.resources || {};
    } catch (err) {
      return {};
    }
  }

  // ── CI Search ─────────────────────────────────────────────────────────────

  async ciSearch(companyId, { types, q, limit, scope } = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    if (types) params.set('types', types);
    if (q) params.set('q', q);
    if (limit) params.set('limit', limit);
    // `scope=authorized` = buscar en TODAS las empresas del usuario, no solo en
    // la activa (conmutador «Todas» del buscador global).
    if (scope) params.set('scope', scope);
    const res = await fetch(`${API_BASE}/ci/search?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    return res.json().catch(() => ({ results: [], total: 0 }));
  }

  // ── Service Groups ────────────────────────────────────────────────────────

  async getServiceGroupsMeta(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-groups/meta?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    return res.json().catch(() => ({}));
  }

  async getServiceGroups(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-groups?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({ groups: [], total: 0 }));
    if (!res.ok) throw new Error(p.error || 'Failed to load service groups');
    return p;
  }

  async createServiceGroup(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-groups?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create service group');
    return p;
  }

  async updateServiceGroup(id, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-groups/${id}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update service group');
    return p;
  }

  async deleteServiceGroup(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-groups/${id}?${params}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete service group');
    return p;
  }

  async getServiceGroupMap(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-groups/${id}/map?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to load group map');
    return p;
  }

  async addServiceGroupMember(groupId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-groups/${groupId}/members?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to add member');
    return p;
  }

  async removeServiceGroupMember(groupId, memberId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-groups/${groupId}/members/${memberId}?${params}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to remove member');
    return p;
  }

  // ── Service Hosts ─────────────────────────────────────────────────────────

  async getServiceHostsMeta(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/services/hosts/meta?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    return res.json().catch(() => ({}));
  }

  async getServiceHosts(svcId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/services/${svcId}/hosts?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({ hosts: [], total: 0 }));
    if (!res.ok) throw new Error(p.error || 'Failed to load hosts');
    return p;
  }

  async createServiceHost(svcId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/services/${svcId}/hosts?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create host');
    return p;
  }

  async deleteServiceHost(svcId, hostId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/services/${svcId}/hosts/${hostId}?${params}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete host');
    return p;
  }

  // ── Active Directory Topology ─────────────────────────────────────────────

  async getADTopology(serviceId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ad-topology/${serviceId}?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to load AD topology');
    return p;
  }

  async updateADTopology(serviceId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ad-topology/${serviceId}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update AD topology');
    return p;
  }

  async getADTopologyDcCandidates(serviceId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ad-topology/${serviceId}/dc-candidates?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to load DC candidates');
    return p;
  }

  // ── Generic Service Topology (vpn, database, rds_farm) ────────────────────

  async getServiceTopology(serviceId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-topology/${serviceId}?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to load topology');
    return p;
  }

  async updateServiceTopology(serviceId, topology, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-topology/${serviceId}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ topology }),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update topology');
    return p;
  }

  async getServiceTopologyServerCandidates(serviceId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-topology/${serviceId}/server-candidates?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({ candidates: [] }));
    return p;
  }

  async getServiceTopologyLocationCandidates(serviceId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-topology/${serviceId}/location-candidates?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({ candidates: [] }));
    return p;
  }

  async getServiceDependencies(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/services/${id}/dependencies?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    return res.json().catch(() => ({ outbound: [], inbound: [] }));
  }

  async createServiceDependency(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/services/dependencies?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create dependency');
    return p;
  }

  async deleteServiceDependency(depId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/services/dependencies/${depId}?${params}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete dependency');
    return p;
  }

  // ── Service Farms ───────────────────────────────────────────────────────

  async getServiceFarmsMeta(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-farms/meta?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    return res.json().catch(() => ({}));
  }

  async getServiceFarms(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') params.set(k, v); });
    const res = await fetch(`${API_BASE}/service-farms?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => []);
    if (!res.ok) throw new Error(p.error || 'Failed to load service farms');
    return p;
  }

  async getServiceFarm(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-farms/${id}?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to load service farm');
    return p;
  }

  async createServiceFarm(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-farms?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create service farm');
    return p;
  }

  async updateServiceFarm(id, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-farms/${id}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update service farm');
    return p;
  }

  async deleteServiceFarm(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-farms/${id}?${params}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete service farm');
    return p;
  }

  async addServiceFarmMember(farmId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-farms/${farmId}/members?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to add member');
    return p;
  }

  async deleteServiceFarmMember(farmId, memberId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-farms/${farmId}/members/${memberId}?${params}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete member');
    return p;
  }

  // ── Backup policies ─────────────────────────────────────────────────────

  async getBackupMeta(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/backup-policies/meta?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    return res.json().catch(() => ({}));
  }

  async getBackupPolicies(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') params.set(k, v); });
    const res = await fetch(`${API_BASE}/backup-policies?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => []);
    if (!res.ok) throw new Error(p.error || 'Failed to load backup policies');
    return p;
  }

  async getBackupRepositories(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/backup-policies/repositories?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to load backup repositories');
    return p;
  }

  async getBackupPolicy(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/backup-policies/${id}?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to load backup policy');
    return p;
  }

  async createBackupPolicy(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/backup-policies?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create backup policy');
    return p;
  }

  async updateBackupPolicy(id, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/backup-policies/${id}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update backup policy');
    return p;
  }

  async deleteBackupPolicy(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/backup-policies/${id}?${params}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete backup policy');
    return p;
  }

  async addBackupTarget(policyId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/backup-policies/${policyId}/targets?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to add target');
    return p;
  }

  async deleteBackupTarget(policyId, targetId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/backup-policies/${policyId}/targets/${targetId}?${params}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete target');
    return p;
  }

  // ── Application assets (DB instances / Web sites) ───────────────────────

  async getAppsMeta(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/apps/meta?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    return res.json().catch(() => ({}));
  }

  async getDatabaseInstances(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') params.set(k, v); });
    const res = await fetch(`${API_BASE}/apps/databases?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => []);
    if (!res.ok) throw new Error(p.error || 'Failed to load database instances');
    return p;
  }

  async createDatabaseInstance(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/apps/databases?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create database instance');
    return p;
  }

  async updateDatabaseInstance(id, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/apps/databases/${id}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update database instance');
    return p;
  }

  async deleteDatabaseInstance(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/apps/databases/${id}?${params}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete database instance');
    return p;
  }

  // Copia de una instancia de BD: réplica del motor, restauración de un backup
  // del propio SQL o clonado de la máquina.
  async copyDatabaseInstance(id, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/apps/databases/${id}/copy?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data || {}),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to copy database instance');
    return p;
  }

  // ── Bases de datos DENTRO de una instancia ──────────────────────────────
  // Dos niveles distintos: la instancia es el motor con su puerto, y dentro de
  // cada instancia viven las bases (odoo_prod, master…).

  async getInstanceDatabases(instanceId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/apps/databases/${instanceId}/databases?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => []);
    if (!res.ok) throw new Error(p.error || 'Failed to load databases');
    return p;
  }

  async createInstanceDatabase(instanceId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/apps/databases/${instanceId}/databases?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data || {}),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create database');
    return p;
  }

  async updateInstanceDatabase(instanceId, rowId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/apps/databases/${instanceId}/databases/${rowId}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data || {}),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update database');
    return p;
  }

  async deleteInstanceDatabase(instanceId, rowId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/apps/databases/${instanceId}/databases/${rowId}?${params}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete database');
    return p;
  }

  // ── Licencias de software (números de serie) ────────────────────────────

  async getLicensesMeta(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/licenses/meta?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    return res.json().catch(() => ({}));
  }

  async getLicenses(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') params.set(k, v); });
    const res = await fetch(`${API_BASE}/licenses?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => []);
    if (!res.ok) throw new Error(p.error || 'Failed to load licenses');
    return p;
  }

  async getLicensesSummary(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/licenses/summary?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    return res.json().catch(() => ({}));
  }

  async createLicense(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/licenses?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data || {}),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create license');
    return p;
  }

  async updateLicense(id, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/licenses/${id}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data || {}),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update license');
    return p;
  }

  async deleteLicense(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/licenses/${id}?${params}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete license');
    return p;
  }

  // ── Inventario de software instalado ───────────────────────────────────

  async getSoftwareMeta(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/software/meta?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    return res.json().catch(() => ({}));
  }

  async getSoftwareInstallations(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.set(k, v);
    });
    const res = await fetch(`${API_BASE}/software/installations?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => []);
    if (!res.ok) throw new Error(p.error || 'Failed to load software inventory');
    return p;
  }

  async createSoftwareInstallation(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/software/installations?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data || {}),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create software installation');
    return p;
  }

  async updateSoftwareInstallation(id, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/software/installations/${id}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data || {}),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update software installation');
    return p;
  }

  async deleteSoftwareInstallation(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/software/installations/${id}?${params}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete software installation');
    return p;
  }

  async syncHostSoftware(hostCiType, hostCiId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/software/installations/sync-host?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ host_ci_type: hostCiType, host_ci_id: hostCiId }),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to sync software inventory');
    return p;
  }

  // ── Distribución de software (Chocolatey) ──────────────────────────────

  async getSoftwarePackages(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/software/packages?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to load software packages');
    return p;
  }

  async getSoftwareOverview(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/software/overview?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to load overview');
    return p;
  }

  async getPackageHosts(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/software/hosts?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to load hosts');
    return p;
  }

  async retryAllSoftwareErrors(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/software/deployments/retry-errors?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data || {}),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to retry deployments');
    return p;
  }

  async cancelSoftwareDeployment(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/software/deployments/${id}/cancel?${params}`, {
      method: 'POST',
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to cancel deployment');
    return p;
  }

  async getSoftwareCatalog(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/software/catalog?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to load catalog');
    return p;
  }

  async importSoftwareCatalog(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/software/catalog/import?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data || {}),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to import catalog');
    return p;
  }

  async createSoftwarePackage(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/software/packages?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data || {}),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create software package');
    return p;
  }

  async updateSoftwarePackage(id, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/software/packages/${id}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data || {}),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update software package');
    return p;
  }

  async deleteSoftwarePackage(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/software/packages/${id}?${params}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete software package');
    return p;
  }

  async deploySoftwarePackage(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/software/deploy?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data || {}),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to deploy software package');
    return p;
  }

  async upgradeAllSoftware(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/software/upgrade-all?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data || {}),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to upgrade packages');
    return p;
  }

  async getSoftwareDeployments(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.set(k, v);
    });
    const res = await fetch(`${API_BASE}/software/deployments?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to load deployments');
    return p;
  }

  async retrySoftwareDeployment(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/software/deployments/${id}/retry?${params}`, {
      method: 'POST',
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to retry deployment');
    return p;
  }

  async getChocoStatus(ciType, ciId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/software/choco/status?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ ci_type: ciType, ci_id: ciId }),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to check Chocolatey');
    return p;
  }

  async getWebSites(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') params.set(k, v); });
    const res = await fetch(`${API_BASE}/apps/web-sites?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => []);
    if (!res.ok) throw new Error(p.error || 'Failed to load web sites');
    return p;
  }

  async createWebSite(data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/apps/web-sites?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create web site');
    return p;
  }

  async updateWebSite(id, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/apps/web-sites/${id}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update web site');
    return p;
  }

  async deleteWebSite(id, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/apps/web-sites/${id}?${params}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete web site');
    return p;
  }

  // ── Floor plan images ──────────────────────────────────────────────────────

  async uploadLocationFloorPlan(locationId, file, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const formData = new FormData();
    formData.append('file', file);
    const { 'Content-Type': _ct, ...authOnly } = this.getAuthHeaders();
    const res = await fetch(`${API_BASE}/locations/${locationId}/floor-plan?${params}`, {
      method: 'POST', headers: authOnly, credentials: 'include', body: formData,
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to upload floor plan');
    return d;
  }

  async deleteLocationFloorPlan(locationId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/locations/${locationId}/floor-plan?${params}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to delete floor plan');
    return d;
  }

  /** Formato y nº de páginas del plano. Los PDF pueden traer varias plantas. */
  async getLocationFloorPlanMeta(locationId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/locations/${locationId}/floor-plan/meta?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'No floor plan available');
    return d.data ?? {};
  }

  /**
   * Descarga el plano como blob. En PDF el backend rasteriza la página pedida
   * (`page`, 1-based) y la sirve como PNG, así se puede pintar en un <img>.
   */
  async fetchFloorPlanBlob(locationId, companyId, page) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    if (page && Number(page) > 1) params.set('page', String(page));
    const res = await fetch(`${API_BASE}/locations/${locationId}/floor-plan/image?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    if (!res.ok) throw new Error('No floor plan available');
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  }

  // ── Anotaciones del plano (textos, líneas y flechas) ─────────────────────

  async getFloorPlanAnnotations(locationId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/locations/${locationId}/floor-plan/annotations?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'No se pudieron cargar las anotaciones');
    return d;
  }

  async createFloorPlanAnnotation(locationId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/locations/${locationId}/floor-plan/annotations?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'No se pudo crear la anotación');
    return d;
  }

  async updateFloorPlanAnnotation(locationId, annotationId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/locations/${locationId}/floor-plan/annotations/${annotationId}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'No se pudo guardar la anotación');
    return d;
  }

  async deleteFloorPlanAnnotation(locationId, annotationId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/locations/${locationId}/floor-plan/annotations/${annotationId}?${params}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'No se pudo borrar la anotación');
    return d;
  }

  // ── Doc Pages (TipTap documentation module) ──────────────────────────────

  async getDocPages(companyId, filters = {}) {
    const params = new URLSearchParams({ company_id: companyId });
    if (filters.q)        params.set('q',        filters.q);
    if (filters.status)   params.set('status',   filters.status);
    if (filters.tag)      params.set('tag',      filters.tag);
    if (filters.doc_type) params.set('doc_type', filters.doc_type);
    const res = await fetch(`${API_BASE}/docs?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    if (!res.ok) throw new Error('Error cargando documentos');
    const d = await res.json();
    return d.data ?? [];
  }

  async uploadDocFile(companyId, file, title, description, tags) {
    const formData = new FormData();
    formData.append('file', file);
    if (title) formData.append('title', title);
    if (description) formData.append('description', description);
    if (tags) formData.append('tags', JSON.stringify(tags));
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE}/docs/upload?company_id=${companyId}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
      body: formData,
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Error al subir archivo'); }
    const d = await res.json();
    return d.data;
  }

  async createDocPage(companyId, payload) {
    const res = await fetch(`${API_BASE}/docs?company_id=${companyId}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Error al crear documento'); }
    const d = await res.json();
    return d.data;
  }

  async getDocPage(id, companyId) {
    const res = await fetch(`${API_BASE}/docs/${id}?company_id=${companyId}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    if (!res.ok) throw new Error('Error cargando documento');
    const d = await res.json();
    return d.data;
  }

  async updateDocPage(id, companyId, payload) {
    const res = await fetch(`${API_BASE}/docs/${id}?company_id=${companyId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Error al actualizar documento'); }
    const d = await res.json();
    return d.data;
  }

  async deleteDocPage(id, companyId) {
    const res = await fetch(`${API_BASE}/docs/${id}?company_id=${companyId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    if (!res.ok) throw new Error('Error al eliminar documento');
    return res.json();
  }

  // Versions
  async listDocPageVersions(id, companyId) {
    const res = await fetch(`${API_BASE}/docs/${id}/versions?company_id=${companyId}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    if (!res.ok) return [];
    const d = await res.json();
    return d.data ?? [];
  }

  async saveDocPageVersion(id, companyId, payload) {
    const res = await fetch(`${API_BASE}/docs/${id}/versions?company_id=${companyId}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Error al guardar versión');
    return res.json();
  }

  async getDocPageVersion(id, ver, companyId) {
    const res = await fetch(`${API_BASE}/docs/${id}/versions/${ver}?company_id=${companyId}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    if (!res.ok) throw new Error('Error cargando versión');
    const d = await res.json();
    return d.data;
  }

  // Links (N:M element relations)
  async getDocPageLinks(id, companyId) {
    const res = await fetch(`${API_BASE}/docs/${id}/links?company_id=${companyId}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    if (!res.ok) return [];
    const d = await res.json();
    return d.data ?? [];
  }

  async addDocPageLink(id, companyId, elementType, elementId, elementName) {
    const res = await fetch(`${API_BASE}/docs/${id}/links?company_id=${companyId}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify({ element_type: elementType, element_id: elementId, element_name: elementName }),
    });
    if (!res.ok) throw new Error('Error al vincular elemento');
    const d = await res.json();
    return d.data;
  }

  async removeDocPageLink(id, linkId, companyId) {
    const res = await fetch(`${API_BASE}/docs/${id}/links/${linkId}?company_id=${companyId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    if (!res.ok) throw new Error('Error al desvincular');
    return res.json();
  }

  // URL references
  async getDocPageUrls(id, companyId) {
    const res = await fetch(`${API_BASE}/docs/${id}/urls?company_id=${companyId}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    if (!res.ok) return [];
    const d = await res.json();
    return d.data ?? [];
  }

  async addDocPageUrl(id, companyId, payload) {
    const res = await fetch(`${API_BASE}/docs/${id}/urls?company_id=${companyId}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Error al añadir URL');
    const d = await res.json();
    return d.data;
  }

  async updateDocPageUrl(id, urlId, companyId, payload) {
    const res = await fetch(`${API_BASE}/docs/${id}/urls/${urlId}?company_id=${companyId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Error al actualizar URL');
    const d = await res.json();
    return d.data;
  }

  async removeDocPageUrl(id, urlId, companyId) {
    const res = await fetch(`${API_BASE}/docs/${id}/urls/${urlId}?company_id=${companyId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    if (!res.ok) throw new Error('Error al eliminar URL');
    return res.json();
  }

  // For smart buttons — get docs linked to an element
  async getDocPagesForElement(elementType, elementId, companyId) {
    const params = new URLSearchParams({ element_type: elementType, element_id: elementId, company_id: companyId });
    const res = await fetch(`${API_BASE}/docs/for-element?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    if (!res.ok) return { data: [], count: 0 };
    return res.json();
  }

  // Element picker search (link panel)
  async searchElementsForDoc(q, companyId) {
    const params = new URLSearchParams({ q, company_id: companyId });
    const res = await fetch(`${API_BASE}/docs/search-elements?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    if (!res.ok) return [];
    const d = await res.json();
    return d.data ?? [];
  }

  // ── Agent: API Keys ───────────────────────────────────────────────────────

  async getAgentApiKeys(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/companies/${companyId}/api-keys`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to fetch API keys');
    return d;
  }

  async createAgentApiKey(companyId, name) {
    const res = await fetch(`${API_BASE}/companies/${companyId}/api-keys`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name }),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to create API key');
    return d;   // d.raw_key is shown only once
  }

  async revokeAgentApiKey(companyId, keyId) {
    const res = await fetch(`${API_BASE}/companies/${companyId}/api-keys/${keyId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to revoke API key');
    return d;
  }

  async deleteAgentApiKey(companyId, keyId) {
    const res = await fetch(`${API_BASE}/companies/${companyId}/api-keys/${keyId}/purge`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to delete API key');
    return d;
  }

  async getCompanyAgentPolicy(companyId) {
    const res = await fetch(`${API_BASE}/companies/${companyId}/agent-policy`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to fetch company agent policy');
    return d;
  }

  async updateCompanyAgentPolicy(companyId, payload) {
    const res = await fetch(`${API_BASE}/companies/${companyId}/agent-policy`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to update company agent policy');
    return d;
  }

  async getAgentBootstrapTokens(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/agent/bootstrap/tokens?${params.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to fetch bootstrap tokens');
    return d;
  }

  async createAgentBootstrapToken(companyId, payload) {
    const res = await fetch(`${API_BASE}/agent/bootstrap/tokens`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ ...payload, company_id: companyId }),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to create bootstrap token');
    return d;
  }

  async revokeAgentBootstrapToken(companyId, tokenId) {
    const res = await fetch(`${API_BASE}/agent/bootstrap/tokens/${tokenId}/revoke`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ company_id: companyId }),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to revoke bootstrap token');
    return d;
  }

  async deleteAgentBootstrapToken(companyId, tokenId, reason) {
    const res = await fetch(`${API_BASE}/agent/bootstrap/tokens/${tokenId}/purge`, {
      method: 'DELETE',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ company_id: companyId, reason }),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to delete bootstrap token');
    return d;
  }

  async getAgentCredentials(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.set(key, value);
    });
    const res = await fetch(`${API_BASE}/agent/credentials?${params.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to fetch agent credentials');
    return d;
  }

  async reissueAgentCredential(companyId, payload) {
    const res = await fetch(`${API_BASE}/agent/credentials/reissue`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ ...payload, company_id: companyId }),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to reissue agent credential');
    return d;
  }

  async revokeAgentCredential(companyId, credentialId, reason) {
    const res = await fetch(`${API_BASE}/agent/credentials/${credentialId}/revoke`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ company_id: companyId, reason }),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to revoke agent credential');
    return d;
  }

  async deleteAgentCredential(companyId, credentialId, reason) {
    const res = await fetch(`${API_BASE}/agent/credentials/${credentialId}/purge`, {
      method: 'DELETE',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ company_id: companyId, reason }),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to delete agent credential');
    return d;
  }

  async getAgentCredentialEvents(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.set(key, value);
    });
    const res = await fetch(`${API_BASE}/agent/credentials/events?${params.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to fetch agent credential events');
    return d;
  }

  // ── Agent: Network collector ──────────────────────────────────────────────

  async getNetworkCollectors(companyId) {
    const res = await fetch(`${API_BASE}/network/collectors?company_id=${companyId}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'No se pudieron cargar los colectores');
    return d;
  }

  async getNetworkCredentials(companyId) {
    const res = await fetch(`${API_BASE}/network/credentials?company_id=${companyId}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'No se pudieron cargar las credenciales de red');
    return d;
  }

  async createNetworkCredential(companyId, payload) {
    const res = await fetch(`${API_BASE}/network/credentials?company_id=${companyId}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'No se pudo crear la credencial de red');
    return d;
  }

  async updateNetworkCredential(companyId, credentialId, payload) {
    const res = await fetch(
      `${API_BASE}/network/credentials/${credentialId}?company_id=${companyId}`,
      {
        method: 'PUT',
        headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      },
    );
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'No se pudo guardar la credencial de red');
    return d;
  }

  async deleteNetworkCredential(companyId, credentialId) {
    const res = await fetch(
      `${API_BASE}/network/credentials/${credentialId}?company_id=${companyId}`,
      { method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include' },
    );
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'No se pudo borrar la credencial de red');
    return d;
  }

  async testNetworkCredential(companyId, credentialId) {
    const res = await fetch(
      `${API_BASE}/network/credentials/${credentialId}/test?company_id=${companyId}`,
      {
        method: 'POST',
        headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      },
    );
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'No se pudo probar la credencial');
    return d;
  }

  async getNetworkScopes(companyId) {
    const res = await fetch(`${API_BASE}/network/scopes?company_id=${companyId}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'No se pudieron cargar los ámbitos');
    return d;
  }

  async createNetworkScope(companyId, payload) {
    const res = await fetch(`${API_BASE}/network/scopes?company_id=${companyId}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'No se pudo crear el ámbito');
    return d;
  }

  async updateNetworkScope(companyId, scopeId, payload) {
    const res = await fetch(`${API_BASE}/network/scopes/${scopeId}?company_id=${companyId}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'No se pudo guardar el ámbito');
    return d;
  }

  async deleteNetworkScope(companyId, scopeId) {
    const res = await fetch(`${API_BASE}/network/scopes/${scopeId}?company_id=${companyId}`, {
      method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'No se pudo borrar el ámbito');
    return d;
  }

  async getNetworkRuns(companyId, limit = 50) {
    const res = await fetch(
      `${API_BASE}/network/runs?company_id=${companyId}&limit=${limit}`,
      { headers: this.getAuthHeaders(), credentials: 'include' },
    );
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'No se pudieron cargar las ejecuciones');
    return d;
  }

  async getNetworkDiscovered(companyId) {
    const res = await fetch(`${API_BASE}/network/discovered?company_id=${companyId}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'No se pudieron cargar los descubiertos');
    return d;
  }

  async triggerNetworkScan(companyId, payload = {}) {
    const res = await fetch(`${API_BASE}/network/scan?company_id=${companyId}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'No se pudo lanzar el barrido');
    return d;
  }

  async updateDiscoveredEndpoint(companyId, endpointId, payload) {
    const res = await fetch(
      `${API_BASE}/network/endpoints/${endpointId}?company_id=${companyId}`,
      {
        method: 'PATCH',
        headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      },
    );
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'No se pudo actualizar el equipo descubierto');
    return d;
  }

  async deleteDiscoveredDevice(companyId, elementId) {
    const res = await fetch(
      `${API_BASE}/network/discovered/${elementId}?company_id=${companyId}`,
      { method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include' },
    );
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'No se pudo borrar el equipo descubierto');
    return d;
  }

  async deleteDiscoveredEndpoint(companyId, endpointId) {
    const res = await fetch(
      `${API_BASE}/network/endpoints/${endpointId}?company_id=${companyId}`,
      { method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include' },
    );
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'No se pudo borrar el equipo final');
    return d;
  }

  async deleteDiscoveredSsid(companyId, ssidId) {
    const res = await fetch(
      `${API_BASE}/network/ssids/${ssidId}?company_id=${companyId}`,
      { method: 'DELETE', headers: this.getAuthHeaders(), credentials: 'include' },
    );
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'No se pudo borrar la SSID descubierta');
    return d;
  }

  // ── Agent: Mapping Profile ────────────────────────────────────────────────

  async getAgentFieldCatalog(ciType = 'server') {
    const params = new URLSearchParams();
    if (ciType) params.set('ci_type', ciType);
    const res = await fetch(`${API_BASE}/agent/field-catalog?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to fetch field catalog');
    return d;
  }

  async getAgentMappingProfile(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/agent/mapping-profile?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to fetch mapping profile');
    return d;
  }

  async saveAgentMappingProfile(companyId, payload) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/agent/mapping-profile?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to save mapping profile');
    return d;
  }

  // ── Agent: Commands ───────────────────────────────────────────────────────

  /**
   * Foto de presencia: agentes que ahora mismo tienen el socket abierto.
   * El estado en vivo llega después por el evento `agent:presence`.
   */
  async getAgentPresence(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/agent/presence?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to fetch presence');
    return d;
  }

  async getAgentCommands(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    const res = await fetch(`${API_BASE}/agent/commands?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to fetch commands');
    return d;
  }

  async enqueueAgentCommand(companyId, payload) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    logAgentApi('enqueue_request', {
      companyId,
      command: payload?.command,
      ciType: payload?.ci_type,
      ciId: payload?.ci_id,
      agentId: maskAgentIdentifier(payload?.agent_id),
    });
    const res = await fetch(`${API_BASE}/agent/commands?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const d = await res.json().catch(() => {
      logAgentApi('enqueue_response_parse_failed', {
        companyId,
        status: res.status,
      });
      return {};
    });
    if (!res.ok) {
      // El backend manda un mensaje util cuando lo tiene (409 por version,
      // 400 por datos que faltan). Sin el, se dice al menos el codigo HTTP:
      // «Failed to enqueue command» a secas no ayuda a nadie.
      throw new Error(d.error || d.message || `No se pudo encolar el comando (HTTP ${res.status})`);
    }
    logAgentApi('enqueue_response', {
      companyId,
      status: res.status,
      commandId: d?.command?.id || null,
      commandStatus: d?.command?.status || null,
    });
    return d;
  }

  async sendAgentUpdateCommand(companyId, payload) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    logAgentApi('update_enqueue_request', {
      companyId,
      ciType: payload?.ci_type,
      ciId: payload?.ci_id,
      agentId: maskAgentIdentifier(payload?.agent_id),
    });
    const res = await fetch(`${API_BASE}/agent/send-update-command?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const d = await res.json().catch(() => {
      logAgentApi('update_enqueue_response_parse_failed', {
        companyId,
        status: res.status,
      });
      return {};
    });
    if (!res.ok) throw new Error(d.error || 'Failed to enqueue update command');
    logAgentApi('update_enqueue_response', {
      companyId,
      status: res.status,
      commandId: d?.command?.id || null,
      commandStatus: d?.command?.status || null,
    });
    return d;
  }

  /**
   * Estado de la última actualización remota del agente de un CI.
   *
   * `state`: idle | updating | updated | up-to-date | unconfirmed | failed.
   * «updated» solo aparece cuando el latido ya reporta la versión objetivo.
   */
  async getAgentUpdateStatus(companyId, { ci_type, ci_id, agent_id } = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    if (ci_type) params.set('ci_type', ci_type);
    if (ci_id) params.set('ci_id', ci_id);
    if (agent_id) params.set('agent_id', agent_id);
    const res = await fetch(`${API_BASE}/agent/update-status?${params}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'No se pudo consultar el estado de actualización');
    return d;
  }

  async getAgentInstallerInfo() {
    const res = await fetch(`${API_BASE}/agent/installer`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to fetch installer info');
    return d;
  }

  async getAgentPackageInfo() {
    const res = await fetch(`${API_BASE}/agent/package`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to fetch package info');
    return d;
  }

  /** MSI publicado (instalación silenciosa / GPO). */
  async getAgentMsiInfo() {
    const res = await fetch(`${API_BASE}/agent/msi`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to fetch MSI info');
    return d;
  }

  async getAgentInventorySnapshots(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.set(key, value);
    });
    const res = await fetch(`${API_BASE}/agent/history/snapshots?${params.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to fetch inventory snapshots');
    return d;
  }

  async getAgentInventorySnapshot(companyId, snapshotId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/agent/history/snapshots/${snapshotId}?${params.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to fetch inventory snapshot detail');
    return d;
  }

  /** Cuánto hay que borrar: {snapshots, commands, events}. */
  async previewAgentHistoryPurge(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/agent/history/purge/preview?${params.toString()}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to preview agent history');
    return d;
  }

  /** Borra el histórico del agente: `{snapshots, commands, events}` a true/false. */
  async purgeAgentHistory(companyId, scopes) {
    const res = await fetch(`${API_BASE}/agent/history/purge`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ ...scopes, company_id: companyId }),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to purge agent history');
    return d;
  }

  async uploadAgentInstaller(file, { version, notes } = {}) {
    const formData = new FormData();
    formData.append('file', file);
    if (version) formData.append('version', version);
    if (notes) formData.append('notes', notes);

    const headers = this.getAuthHeaders();
    delete headers['Content-Type'];

    const res = await fetch(`${API_BASE}/agent/installer`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: formData,
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to upload installer');
    return d;
  }

  async uploadAgentPackage(file, { version, notes } = {}) {
    const formData = new FormData();
    formData.append('file', file);
    if (version) formData.append('version', version);
    if (notes) formData.append('notes', notes);

    const headers = this.getAuthHeaders();
    delete headers['Content-Type'];

    const res = await fetch(`${API_BASE}/agent/package`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: formData,
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to upload package');
    return d;
  }

  async uploadAgentMsi(file, { version, notes } = {}) {
    const formData = new FormData();
    formData.append('file', file);
    if (version) formData.append('version', version);
    if (notes) formData.append('notes', notes);

    const headers = this.getAuthHeaders();
    delete headers['Content-Type'];

    const res = await fetch(`${API_BASE}/agent/msi`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: formData,
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to upload MSI');
    return d;
  }

  async getAgentCommandStatus(companyId, commandId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/agent/commands/${commandId}?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const d = await res.json().catch(() => {
      logAgentApi('command_status_parse_failed', {
        companyId,
        commandId,
        status: res.status,
      });
      return {};
    });
    if (!res.ok) throw new Error(d.error || 'Failed to fetch command status');
    if (!d || typeof d !== 'object' || !d.command) {
      logAgentApi('command_status_invalid_shape', {
        companyId,
        commandId,
        status: res.status,
        payload: d,
      });
      throw new Error('Invalid command status response from backend');
    }
    logAgentApi('command_status_response', {
      companyId,
      commandId,
      status: res.status,
      commandStatus: d.command?.status || null,
    });
    return d;
  }

  async triggerOfflineDetection(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/agent/mark-offline?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({}),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to trigger offline detection');
    return d;
  }

  // ── Peripheral Types ──────────────────────────────────────────────────────

  async getPeripheralTypes(companyId) {
    const res = await fetch(`${API_BASE}/peripherals/types?company_id=${companyId}`, {
      headers: this.getAuthHeaders(),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to fetch peripheral types');
    return d;
  }

  async createPeripheralType(data, companyId) {
    const res = await fetch(`${API_BASE}/peripherals/types?company_id=${companyId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() },
      body: JSON.stringify(data),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to create peripheral type');
    return d;
  }

  async deletePeripheralType(id, companyId) {
    const res = await fetch(`${API_BASE}/peripherals/types/${id}?company_id=${companyId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to delete peripheral type');
    return d;
  }

  // ── Peripherals ───────────────────────────────────────────────────────────

  async getPeripherals(filters = {}) {
    const params = new URLSearchParams(filters).toString();
    const res = await fetch(`${API_BASE}/peripherals?${params}`, {
      headers: this.getAuthHeaders(),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to fetch peripherals');
    return d;
  }

  async getPeripheral(id, companyId) {
    const res = await fetch(`${API_BASE}/peripherals/${id}?company_id=${companyId}`, {
      headers: this.getAuthHeaders(),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to fetch peripheral');
    return d;
  }

  async createPeripheral(data, companyId) {
    const res = await fetch(`${API_BASE}/peripherals?company_id=${companyId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() },
      body: JSON.stringify(data),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to create peripheral');
    return d;
  }

  async updatePeripheral(id, data, companyId) {
    const res = await fetch(`${API_BASE}/peripherals/${id}?company_id=${companyId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() },
      body: JSON.stringify(data),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to update peripheral');
    return d;
  }

  async deletePeripheral(id, companyId) {
    const res = await fetch(`${API_BASE}/peripherals/${id}?company_id=${companyId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Failed to delete peripheral');
    return d;
  }

  /**
   * Importa periféricos ya mapeados desde Excel/CSV.
   * `payload = { rows, update_existing, create_types }`; el fichero lo lee el
   * frontend y aquí solo viajan los campos canónicos del modelo.
   */
  async importPeripherals(companyId, payload) {
    const res = await fetch(`${API_BASE}/peripherals/import?company_id=${companyId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() },
      credentials: 'include',
      body: JSON.stringify(payload || {}),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || d.message || 'Failed to import peripherals');
    return d;
  }

  // ── Service Audit ────────────────────────────────────────────────────────

  async getServiceAuditConfig(serviceId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-audit/${serviceId}/config?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch audit config');
    return p;
  }

  async updateServiceAuditConfig(serviceId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-audit/${serviceId}/config?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update audit config');
    return p;
  }

  async getServiceAuditLatest(serviceId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-audit/${serviceId}/latest?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to fetch audit reports');
    return p;
  }

  // ── Service Designer (generic graph engine) ──────────────────────────────

  async sdListServices(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-designer/services?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to list designer services');
    return p;
  }

  async sdListRelationTypes(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-designer/relation-types?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to list relation types');
    return p;
  }

  async sdCreateRelationType(companyId, data) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-designer/relation-types?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create relation type');
    return p;
  }

  async sdUpdateRelationType(rtId, companyId, data) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-designer/relation-types/${rtId}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update relation type');
    return p;
  }

  async sdGetGraph(serviceId, companyId, depth) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    if (depth !== undefined && depth !== null && depth !== '') params.set('depth', depth);
    const res = await fetch(`${API_BASE}/service-designer/services/${serviceId}/graph?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to load graph');
    return p;
  }

  async sdGetCiGraph(ciType, ciId, companyId, opts = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    params.set('depth', String(opts.depth || 1));
    params.set('direction', opts.direction || 'both');
    params.set('storage', opts.storage === false ? '0' : '1');
    const res = await fetch(`${API_BASE}/service-designer/ci/${ciType}/${ciId}/graph?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to load element graph');
    return p;
  }

  // Miembros del diseño de un servicio (con su nombre resuelto): la ficha los
  // lista sin tener que abrir el Diseñador.
  async sdGetMembers(serviceId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-designer/services/${serviceId}/members?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to load members');
    return p.data || [];
  }

  async sdAddMember(serviceId, companyId, data) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-designer/services/${serviceId}/members?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to add member');
    return p;
  }

  // Componente lógico / agrupador (spec §8): solo se crea una etiqueta lógica, no un CI físico.
  async sdAddLogicalMember(serviceId, companyId, ciId, label, icon, detail) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-designer/services/${serviceId}/members?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        ci_type: 'logical_component', ci_id: ciId, is_logical: true,
        logical_label: label, logical_icon: icon || null, logical_detail: detail || null,
      }),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to add logical component');
    return p;
  }

  async sdRemoveMember(serviceId, memberId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-designer/services/${serviceId}/members/${memberId}?${params}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to remove member');
    return p;
  }

  async sdConnect(serviceId, companyId, data) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-designer/services/${serviceId}/connect?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to connect CIs');
    return p;
  }

  async sdUpdateRelation(relationId, data, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-designer/relations/${relationId}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update relation');
    return p;
  }

  async sdDeleteRelation(relationId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-designer/relations/${relationId}?${params}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete relation');
    return p;
  }

  async sdSaveLayout(serviceId, companyId, positions, hidden, collapsed, hiddenDerived) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const body = {};
    if (positions !== undefined) body.positions = positions;
    if (hidden !== undefined) body.hidden = hidden;
    if (collapsed !== undefined) body.collapsed = collapsed;
    if (hiddenDerived !== undefined) body.hidden_derived = hiddenDerived;
    const res = await fetch(`${API_BASE}/service-designer/services/${serviceId}/layout?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to save layout');
    return p;
  }

  async sdListModels(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-designer/models?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to list models');
    return p;
  }

  async sdCreateModel(companyId, data) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-designer/models?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create model');
    return p;
  }

  async sdApplyModel(serviceId, modelId, companyId, roleBindings) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-designer/models/${modelId}/apply?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ service_id: Number(serviceId), role_bindings: roleBindings || {} }),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to apply model');
    return p;
  }

  async sdSearchCIs(companyId, types, q, limit = 20, filter) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    if (types && types.length) params.set('types', types.join(','));
    if (q) params.set('q', q);
    params.set('limit', String(limit));
    if (filter && Object.keys(filter).length) params.set('filter', JSON.stringify(filter));
    const res = await fetch(`${API_BASE}/ci/search?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to search CIs');
    // The CI search endpoint returns { results: [...] } (not { data: [...] })
    return p.results ? { data: p.results } : p;
  }

  // ── Service Wizards (guided assistants) ────────────────────────────────────
  async swList(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-wizards?${params}`, {
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to list wizards');
    return p;
  }

  async swCreate(companyId, data) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-wizards?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to create wizard');
    return p;
  }

  async swUpdate(companyId, wizardId, data) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-wizards/${wizardId}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to update wizard');
    return p;
  }

  async swDelete(companyId, wizardId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-wizards/${wizardId}?${params}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to delete wizard');
    return p;
  }

  async swResync(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-wizards/resync?${params}`, {
      method: 'POST',
      headers: this.getAuthHeaders(), credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to resync wizards');
    return p;
  }

  async swPreview(companyId, wizardId, body) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-wizards/${wizardId}/preview?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to preview wizard');
    return p;
  }

  async swApply(companyId, wizardId, body) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/service-wizards/${wizardId}/apply?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'Failed to apply wizard');
    return p;
  }

  // ── Preferencias de columnas (orden + visibilidad por usuario) ──────────
  async getColumnPreferences(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ui-preferences/columns?${params}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudieron cargar las columnas');
    return p.data || { columns: {} };
  }

  async saveColumnPreference(companyId, listKey, order, visible) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/ui-preferences/columns?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ list: listKey, order, visible }),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo guardar el orden de columnas');
    return p.data;
  }

  // ── Contactos ────────────────────────────────────────────────────────────
  async getContacts(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.set(key, value);
    });
    const res = await fetch(`${API_BASE}/contacts?${params}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudieron cargar los contactos');
    return p;
  }

  async getContactsMeta() {
    const res = await fetch(`${API_BASE}/contacts/meta`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo cargar el catálogo de contactos');
    return p.data;
  }

  async getContact(contactId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/contacts/${contactId}?${params}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo cargar el contacto');
    return p.data;
  }

  async createContact(payload, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/contacts?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo crear el contacto');
    return p.data;
  }

  async updateContact(contactId, payload, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/contacts/${contactId}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo guardar el contacto');
    return p.data;
  }

  async deleteContact(contactId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/contacts/${contactId}?${params}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo eliminar el contacto');
    return p;
  }

  async cloneContact(contactId, companyId, payload = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/contacts/${contactId}/clone?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo copiar el contacto');
    return p.data;
  }

  async getContactsForObject(objectType, objectId, companyId) {
    const params = new URLSearchParams({ object_type: objectType, object_id: objectId });
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/contacts/for-object?${params}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudieron cargar los contactos del elemento');
    return p.data || [];
  }

  async addContactLink(contactId, payload, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/contacts/${contactId}/links?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo vincular el contacto');
    return p.data;
  }

  async updateContactLink(contactId, linkId, payload, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/contacts/${contactId}/links/${linkId}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo actualizar el vínculo');
    return p.data;
  }

  async deleteContactLink(contactId, linkId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/contacts/${contactId}/links/${linkId}?${params}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo quitar el vínculo');
    return p;
  }

  // ── DNS ──────────────────────────────────────────────────────────────────
  async getDnsProviders() {
    const res = await fetch(`${API_BASE}/dns/providers`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudieron cargar los proveedores DNS');
    return p;
  }

  async createDnsProvider(payload) {
    const res = await fetch(`${API_BASE}/dns/providers`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo crear el proveedor');
    return p.data;
  }

  async updateDnsProvider(providerId, payload) {
    const res = await fetch(`${API_BASE}/dns/providers/${providerId}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo guardar el proveedor');
    return p.data;
  }

  async deleteDnsProvider(providerId) {
    const res = await fetch(`${API_BASE}/dns/providers/${providerId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo eliminar el proveedor');
    return p;
  }

  async testDnsProvider(providerId) {
    const res = await fetch(`${API_BASE}/dns/providers/${providerId}/test`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo probar el proveedor');
    return p;
  }

  async getDnsProviderZones(providerId) {
    const res = await fetch(`${API_BASE}/dns/providers/${providerId}/zones`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudieron leer las zonas de OVH');
    return p;
  }

  async importDnsDomains(payload) {
    const res = await fetch(`${API_BASE}/dns/domains/import`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudieron importar las zonas');
    return p;
  }

  async quoteDnsDomain(payload) {
    const res = await fetch(`${API_BASE}/dns/quote`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || p.message || 'No se pudo consultar la disponibilidad');
    return p;
  }

  async registerDnsDomain(payload) {
    const res = await fetch(`${API_BASE}/dns/register`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo registrar el dominio en OVH');
    return p;
  }

  // ── Certificados TLS ─────────────────────────────────────────────────────
  async getCertificates(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    const res = await fetch(`${API_BASE}/certificates?${params}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudieron cargar los certificados');
    return p;
  }

  async getCertificate(certificateId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/certificates/${certificateId}?${params}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo cargar el certificado');
    return p.data;
  }

  async createCertificate(payload, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/certificates?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo crear el certificado');
    return p.data;
  }

  async updateCertificate(certificateId, payload, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/certificates/${certificateId}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo guardar el certificado');
    return p.data;
  }

  async deleteCertificate(certificateId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/certificates/${certificateId}?${params}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo eliminar el certificado');
    return p;
  }

  /** Lanza la emisión/renovación con Let's Encrypt (solo superadmin). */
  async issueCertificate(certificateId, { staging = false } = {}) {
    const res = await fetch(`${API_BASE}/certificates/${certificateId}/issue`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ staging }),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo lanzar la emisión');
    return p;
  }

  /** Renueva todos los certificados emitidos por la CMDB que caducan pronto. */
  async renewDueCertificates(days = 30) {
    const res = await fetch(`${API_BASE}/certificates/renew-due?days=${days}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: '{}',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudieron renovar los certificados');
    return p;
  }

  /** Descarga la cadena pública (`cert`) o la clave privada (`key`). */
  async downloadCertificate(certificateId, what = 'cert') {
    const extension = what === 'key' ? 'key' : 'crt';
    return this.downloadAuthedFile(
      `/api/certificates/${certificateId}/download?what=${what}`,
      `certificado.${extension}`,
    );
  }

  /** Trae a la CMDB los certificados SSL de OVH (solo superadmin). */
  async syncCertificates(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/certificates/sync?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: '{}',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudieron sincronizar los certificados de OVH');
    return p;
  }

  async getDnsDomains(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    const res = await fetch(`${API_BASE}/dns/domains?${params}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudieron cargar los dominios');
    return p;
  }

  async getDnsDomain(domainId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/dns/domains/${domainId}?${params}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo cargar el dominio');
    return p.data;
  }

  async createDnsDomain(payload, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/dns/domains?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo crear el dominio');
    return p.data;
  }

  async updateDnsDomain(domainId, payload, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/dns/domains/${domainId}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo guardar el dominio');
    return p.data;
  }

  async deleteDnsDomain(domainId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/dns/domains/${domainId}?${params}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo eliminar el dominio');
    return p;
  }

  async syncDnsDomain(domainId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/dns/domains/${domainId}/sync?${params}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo sincronizar la zona');
    return p;
  }

  async getDnsRecords(domainId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/dns/domains/${domainId}/records?${params}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudieron cargar los registros');
    return p.data || [];
  }

  async createDnsRecord(domainId, payload, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/dns/domains/${domainId}/records?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo crear el registro');
    return p;
  }

  async updateDnsRecord(domainId, recordId, payload, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/dns/domains/${domainId}/records/${recordId}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo guardar el registro');
    return p;
  }

  async deleteDnsRecord(domainId, recordId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/dns/domains/${domainId}/records/${recordId}?${params}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo borrar el registro');
    return p;
  }

  async checkDnsAvailability(domain, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/dns/check/availability?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ domain }),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo comprobar el dominio');
    return p.data;
  }

  async checkDnsDomain(domain, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/dns/check/domain?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ domain }),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo comprobar el dominio');
    return p.data;
  }

  async getDnsCheckHistory(companyId, domain) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    if (domain) params.set('domain', domain);
    const res = await fetch(`${API_BASE}/dns/check/history?${params}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo cargar el histórico');
    return p.data || [];
  }

  // ── Cámaras de seguridad ─────────────────────────────────────────────────
  async getCamerasMeta() {
    const res = await fetch(`${API_BASE}/cameras/meta`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo cargar el catálogo de cámaras');
    return p.data;
  }

  async getCameras(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.set(key, value);
    });
    const res = await fetch(`${API_BASE}/cameras?${params}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudieron cargar las cámaras');
    return p;
  }

  async getCamera(cameraId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/cameras/${cameraId}?${params}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo cargar la cámara');
    return p.data;
  }

  async createCamera(payload, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/cameras?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo crear la cámara');
    return p.data;
  }

  async updateCamera(cameraId, payload, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/cameras/${cameraId}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo guardar la cámara');
    return p.data;
  }

  async deleteCamera(cameraId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/cameras/${cameraId}?${params}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo eliminar la cámara');
    return p;
  }

  async positionCamera(cameraId, payload, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/cameras/${cameraId}/position?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo guardar la posición');
    return p.data;
  }

  async getRecorders(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.set(key, value);
    });
    const res = await fetch(`${API_BASE}/cameras/recorders?${params}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudieron cargar los grabadores');
    return p;
  }

  async getRecorder(recorderId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/cameras/recorders/${recorderId}?${params}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo cargar el grabador');
    return p.data;
  }

  async createRecorder(payload, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/cameras/recorders?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo crear el grabador');
    return p.data;
  }

  async updateRecorder(recorderId, payload, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/cameras/recorders/${recorderId}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo guardar el grabador');
    return p.data;
  }

  async deleteRecorder(recorderId, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/cameras/recorders/${recorderId}?${params}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo eliminar el grabador');
    return p;
  }

  async positionRecorder(recorderId, payload, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/cameras/recorders/${recorderId}/position?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo guardar la posición');
    return p.data;
  }

  async getCamerasTopology(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/cameras/topology?${params}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo cargar la topología de cámaras');
    return p.data;
  }

  // ── Importador genérico ──────────────────────────────────────────────────
  async getImportSchema(entity) {
    const res = await fetch(`${API_BASE}/import/${entity}/schema`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo cargar el esquema de importación');
    return p.data;
  }

  async runImport(entity, rows, updateExisting, companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/import/${entity}?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ rows, update_existing: updateExisting }),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo importar');
    return p.data;
  }

  // ── Dashboard de seguridad ───────────────────────────────────────────────
  async getSecurityDashboard(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/dashboard/security?${params}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo cargar el resumen de seguridad');
    return p.data;
  }

  // ── Planes de trabajo sobre el parque ────────────────────────────────────
  /** Serie temporal de CPU/RAM/disco/red de una estación de trabajo. */
  async getWorkstationMetrics(workstationId, companyId, hours = 6) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    params.set('hours', hours);
    const res = await fetch(
      `${API_BASE}/workstations/${workstationId}/metrics?${params}`,
      { headers: this.getAuthHeaders(), credentials: 'include' },
    );
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudieron cargar las métricas');
    return p;
  }

  async getWorkPlans(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    const res = await fetch(`${API_BASE}/work-plans?${params}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudieron cargar los planes');
    return p.plans || [];
  }

  async getWorkPlan(companyId, planId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/work-plans/${planId}?${params}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo cargar el plan');
    return p;
  }

  async createWorkPlan(companyId, payload) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/work-plans?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo crear el plan');
    return p.plan;
  }

  async updateWorkPlan(companyId, planId, payload) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/work-plans/${planId}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo guardar el plan');
    return p.plan;
  }

  async deleteWorkPlan(companyId, planId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/work-plans/${planId}?${params}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo borrar el plan');
    return p;
  }

  async evaluateWorkPlan(companyId, planId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/work-plans/${planId}/evaluate?${params}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo recalcular el plan');
    return p;
  }

  async getWorkPlanTemplates(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/work-plans/templates?${params}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudieron cargar las plantillas');
    return p.templates || [];
  }

  /** Tipos de elemento (estación, servidor, VM…) y campos que se pueden comprobar. */
  async getWorkPlanCatalog(companyId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/work-plans/catalog?${params}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo cargar el catálogo');
    return p.types || [];
  }

  /** Elementos seleccionables para aplicar un plan a una lista concreta. */
  async getWorkPlanTargets(companyId, ciTypes) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    params.set('ci_types', (ciTypes || ['workstation']).join(','));
    const res = await fetch(`${API_BASE}/work-plans/targets?${params}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudieron cargar los elementos');
    return p.targets || [];
  }

  async createWorkPlanTemplate(companyId, payload) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/work-plans/templates?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo crear la plantilla');
    return p.template;
  }

  async updateWorkPlanTemplate(companyId, templateId, payload) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/work-plans/templates/${templateId}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo guardar la plantilla');
    return p.template;
  }

  async deleteWorkPlanTemplate(companyId, templateId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/work-plans/templates/${templateId}?${params}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo borrar la plantilla');
    return p;
  }

  async saveWorkPlanAsTemplate(companyId, planId, payload = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/work-plans/${planId}/save-as-template?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo guardar la plantilla');
    return p.template;
  }

  async markWorkPlanStep(companyId, stepId, payload) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/work-plans/steps/${stepId}/mark?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo marcar el paso');
    return p;
  }

  async runWorkPlanStep(companyId, stepId, payload) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/work-plans/steps/${stepId}/run?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo lanzar el comando');
    return p;
  }

  async addWorkPlanStep(companyId, planId, payload) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/work-plans/${planId}/steps?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo añadir el paso');
    return p;
  }

  async deleteWorkPlanStep(companyId, stepId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/work-plans/steps/${stepId}?${params}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo borrar el paso');
    return p;
  }

  async updateWorkPlanStep(companyId, stepId, payload) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/work-plans/steps/${stepId}?${params}`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo guardar el paso');
    return p;
  }

  /** Cambia el orden de las columnas (los pasos) de un plan. */
  async reorderWorkPlanSteps(companyId, planId, order) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/work-plans/${planId}/steps/reorder?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ order }),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudo cambiar el orden de los pasos');
    return p;
  }

  /** Alta rápida de estaciones desde un plan (código = nombre). */
  async createWorkPlanElements(companyId, planId, payload) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const res = await fetch(`${API_BASE}/work-plans/${planId}/create-elements?${params}`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudieron crear los equipos');
    return p;
  }

  /** Próximos códigos de nomenclatura SIN consumir (para revisarlos antes de crear). */
  async previewNamingCodes(companyId, { type = 'workstation', count = 1, locationId = null } = {}) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    params.set('type', type);
    params.set('count', String(count));
    if (locationId) params.set('location_id', locationId);
    const res = await fetch(`${API_BASE}/naming/preview?${params}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudieron calcular los códigos');
    return p.data?.suggested_names || [];
  }

  async getWorkPlansForCi(companyId, ciType, ciId) {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    params.set('ci_type', ciType || 'workstation');
    params.set('ci_id', ciId);
    const res = await fetch(`${API_BASE}/work-plans/for-ci?${params}`, {
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    const p = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(p.error || 'No se pudieron cargar los planes del equipo');
    return p.plans || [];
  }
}

export default new APIService();
